import { useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Autocomplete, Box, Button, Divider, Drawer, Grid, InputLabel, SwipeableDrawer, TextField, Typography } from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import validator from 'validator';
import { debounce } from 'lodash';

import globalStyles from "../../../../assets/styles/global.module.scss";
import styles from "../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/modifyWhatsAppTemplate.module.scss";
import formStyles from "../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/whatsAppTemplateForm.module.scss";

import { getFileTypeImgIcon, hasTemplateManageAccess } from "../../../../utils/constants";
import { useTemplateLibraryStore } from "../../../../store/templateLibraryStore";
import { IntegrationContext, IntegrationContextPayload } from "../../../../context/integrationContext";
import { createWhatsappTemplateButton, createWhatsappTemplateButtonData, createWhatsappTemplateButtonSubFieldData, createWhatsappTemplatePayloadForTemplateLibrary, getWhatsappTemplateButtonSubTypeFieldReponse, getWhatsappTemplateButtonSubTypeReponse, getWhatsappTemplateButtonTypeReponse, whatsappCarouselCardData, whatsappTemplateButtonSubTypeFieldData } from "../../../../models/axiosModel";

import axios from "axios";
import agent from "../../../../services/api";

import { UserContext, UserContextPayload } from "../../../../context/userContext";
import { ALLOWED_MEDIA_HEADER_TYPES_CAROUSEL, createBasicTemplateFromScratch, createEmptyLocationHeaderVariables, DEFAULT_BUTTON_TEXT_TOOLTIP, DEFAULT_EXPIRATION_MINUTES, DUPLICATE_BUTTON_TEXT_ERROR, getButtonTypeIdByName, getCopyTemplateDisplayName, getMaxButtonsCount, hasDuplicateButtonText, isInvalidExportFile, isInvalidUrl, MIN_CAROUSEL_CARDS, normalizeTemplatePayloadForDraft, getClearedTemplateFooter, getClearedTemplateHeader } from "../constant";
import { getVendorField } from "../../../../utils/whiteLabelling";
import { canUpdateTemplate, getDefaultValueOfFlagAllowTracking, getTotalButtonsForBasicTemplate, getValidationsFromInteractiveType, getVariableRegex, isBasicTemplate, isInteractiveTypeMessageCtaUrl, isInteractiveTypeMessageFlow, isInteractiveTypeMessageList, CAROUSEL_MAX_CHARS, maxChars, maxFooterChars, maximumAllowedButtons, TEMPLATE_BUTTON_FIELDS, validateVariableKeys, getTemplateCategoryId, TEMPLATE_CATEGORY, isTemplateCategoryAuthentication, renderAuthenticationTemplateFooterText, RENDER_SECURITY_RECOMMENDATION_APPEND_TEXT, BTN_SUB_TYPE_NAMES, isCarouselTemplate, isCatalogTemplate, BTN_TYPE_NAMES, isInteractiveTypeMessageRequestContactInfo, hasShareContactInfoButton } from "../../channels/global/whatsapp/templates/utils";
import { useInsertVariableAtCursorPosition } from "../../../../hooks/useInsertVariableAtCursorPosition";
import { useCarouselFunctions } from "../utils/useCarouselFunctions";
import Required from "../../../utility/required";
import GlobalButton from "../../../utility/globalButton/button";
import TouchTooltip from "../utils/TouchTooltip";

import WhatsAppTemplateForm from "./WhatsAppTemplateForm/WhatsAppTemplateForm";
import WhatsAppTemplatePreview from "../WhatsAppTemplatePreview/WhatsAppTemplatePreview";
import ModifyWhatsAppTemplateHeader from "./ModifyWhatsAppTemplateHeader";
import CarouselTemplateForm from "./CarouselTemplateForm/CarouselTemplateForm";
import CarouselTemplatePreview from "../CarouselTemplatePreview/CarouselTemplatePreview";
import { useGenericStore } from "../../../../store/genericStore";
import { useShallow } from "zustand/react/shallow";

const ModifyWhatsAppTemplate = () => {

    const { setFlagLoading, flagLoading, handleError, vendorData, apiRoutesPermissionAssignment, userDetail } = useContext(UserContext) as UserContextPayload;
    const { integrationListNewStructure, getMetaIntegrationList } = useContext(IntegrationContext) as IntegrationContextPayload;
    
    const formOpen = useTemplateLibraryStore((state) => state.modifyTemplate.formOpen);
    const setFormOpen = useTemplateLibraryStore((state) => state.modifyTemplate.setFormOpen);

    const getWhatsappTemplateTypesApiCall = useTemplateLibraryStore((store) => store.getWhatsappTemplateTypesApiCall);
    const getWhatsappTemplateHeaderTypesApiCall = useTemplateLibraryStore((store) => store.getWhatsappTemplateHeaderTypesApiCall);
    const getWhatsappTemplateDynamicVarTypesApiCall = useTemplateLibraryStore((store) => store.getWhatsappTemplateDynamicVarTypesApiCall);
    const getWhatsappTemplateButtonTypeApiCall = useTemplateLibraryStore((store) => store.getWhatsappTemplateButtonTypeApiCall);
    const getInteractiveTypeListApiCall = useTemplateLibraryStore((store) => store.getInteractiveTypeListApiCall);

    const selectedTemplate = useTemplateLibraryStore((state) => state.selectedTemplate);
    const setSelectedTemplate = useTemplateLibraryStore((state) => state.setSelectedTemplate);
    const createTemplatePayload = useTemplateLibraryStore((state) => state.createTemplatePayload);
    const templateStatusDetails = useTemplateLibraryStore((state) => state.templateStatusDetails);
    const setTemplateStatusDetails = useTemplateLibraryStore((state) => state.setTemplateStatusDetails);
    const setCreateTemplatePayload = useTemplateLibraryStore((state) => state.setCreateTemplatePayload);
    const whatsappTemplateTypes = useTemplateLibraryStore((state) => state.whatsappTemplateTypes);
    const whatsappTemplateDynamicVarTypes = useTemplateLibraryStore((state) => state.whatsappTemplateDynamicVarTypes);
    const files = useTemplateLibraryStore((state) => state.files) ?? [];
    const setFiles = useTemplateLibraryStore((state) => state.setFiles);
    const whatsappTemplateHeaderTypes = useTemplateLibraryStore((state) => state.whatsappTemplateHeaderTypes); 
    const setExistingHeaderTemplateVariableData = useTemplateLibraryStore((state) => state.setExistingHeaderTemplateVariableData);
    const whatsappTemplateButtonGenericData = useTemplateLibraryStore((state) => state.whatsappTemplateButtonGenericData);
    const interactiveTypeList = useTemplateLibraryStore((state) => state.interactiveTypeList);
    const existingTemplateData = useTemplateLibraryStore((state) => state.existingTemplateData);
    const setExistingPayloadData = useTemplateLibraryStore((state) => state.setExistingPayloadData);
    const setExistingTemplateData = useTemplateLibraryStore((state) => state.setExistingTemplateData);
    const setInvalidPayloadObjectId = useTemplateLibraryStore((state) => state.setInvalidPayloadObjectId);
    const templateLibraryDraftList = useTemplateLibraryStore((state) => state.templateLibraryDraftList);
    const getTemplateLibraryDraftListApiCall = useTemplateLibraryStore((state) => state.getTemplateLibraryDraftListApiCall);
    const templateLibraryCategoryList = useTemplateLibraryStore((state) => state.templateLibraryCategoryList);
    const getTemplateLibraryCategoryListApiCall = useTemplateLibraryStore((state) => state.getTemplateLibraryCategoryListApiCall);

    const setHasUnsavedChanges = useTemplateLibraryStore((state) => state.setHasUnsavedChanges);

    const importTemplatePayload = useTemplateLibraryStore((state) => state.importTemplatePayload);
    const setImportTemplatePayload = useTemplateLibraryStore((state) => state.setImportTemplatePayload);
    const setImportTemplateName = useTemplateLibraryStore((state) => state.setImportTemplateName);
    const setImportTemplateJson = useTemplateLibraryStore((state) => state.setImportTemplateJson);
    const setImportTemplateFile = useTemplateLibraryStore((state) => state.setImportTemplateFile);
    const setImportTemplateFileName = useTemplateLibraryStore((state) => state.setImportTemplateFileName);
    const setImportTemplateSelectedTab = useTemplateLibraryStore((state) => state.setImportTemplateSelectedTab);
    const addExpiryTimeForCode = useTemplateLibraryStore((state) => state.addExpiryTimeForCode);
    const setAddExpiryTimeForCode = useTemplateLibraryStore((state) => state.setAddExpiryTimeForCode);

    const inValidId = useTemplateLibraryStore((state) => state.inValidId) ?? [];
    const setInvalidId = useTemplateLibraryStore((state) => state.setInvalidId);

    const setTemplateStatus = useTemplateLibraryStore((state) => state.setTemplateStatus);

    const { showSuccess, showError } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess,
        showError: state.showError
    })))

    const { getExistingCardsPayload, isScreenBelow1250, isMobileScreen, isScreenBelow1600 } = useCarouselFunctions();

    const [text, setText] = useState('');
    const [footerText, setFooterText] = useState('');
    const [carouselHeaderText, setCarouselHeaderText] = useState('');
    const [progress, setProgress] = useState({ percent: 0, filename: '' });
    const [isFileUploaded, setIsFileUploaded] = useState<boolean>(false);
    const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('bottom');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [publishedFlowList, setPublishedFlowList] = useState<any>(null);

    const [isEditMode, setIsEditMode] = useState<boolean>(isMobileScreen ? true : false);
    const [isPageReady, setIsPageReady] = useState(false);

    const initialPayloadRef = useRef<createWhatsappTemplatePayloadForTemplateLibrary | null>(null);
    // const previewHoverWrapRef = useRef<HTMLDivElement | null>(null);

    const [carouselInvalidId, setCarouselInvalidId] = useState<any>([
        {
            index: 0,
            inValidIds: []
        },
        {
            index: 1,
            inValidIds: []
        }
    ]);
    const [actions, setActions] = useState<any>([]);
    const inValidIdRef = useRef<any[]>([]);

    const headerTextareaRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    let id = searchParams.get('id');
    let template_id = searchParams.get('template_id');
    let draft_id = searchParams.get('draft_id');
    let copy_template = searchParams.get('copy_template');
    const previousDraftIdRef = useRef<string | null>(draft_id);
    const previousTemplateIdRef = useRef<string | null>(template_id);

    const innerHeight = (window.innerHeight - 100);

    // const totalButtons = getButtonCount(createTemplatePayload);
    // const hasTriggeredHoverRef = useRef(false);

    const isAuthenticationTemplate = isTemplateCategoryAuthentication(templateLibraryCategoryList, createTemplatePayload?.whatsapp_template_data?.whatsapp_template_category_id);

    const shouldShowPreviewEditModeButtons = !(template_id 
        && (isCarouselTemplate(whatsappTemplateTypes, createTemplatePayload?.whatsapp_template_type_id) 
            || isCatalogTemplate(whatsappTemplateTypes, createTemplatePayload?.whatsapp_template_type_id)
            || isAuthenticationTemplate
        ))

    const [carouselFiles, setCarouselFiles] = useState<any>([{
        index: 0,
        carousels: []
    }, {
        index: 1,
        carousels: []
    }]);
    
    const { insertVariable, registerTextarea } = useInsertVariableAtCursorPosition();

    // useLayoutEffect(() => {
    //     const element = previewHoverWrapRef.current;
    //     if (!element) return;
    
    //     const handleMouseEnterOnce = () => {
    //         if (hasTriggeredHoverRef.current) return;
    //         hasTriggeredHoverRef.current = true;
    //         setHover(true);
    //     };
    
    //     element.addEventListener("mouseenter", handleMouseEnterOnce);
    
    //     return () => {
    //         element.removeEventListener("mouseenter", handleMouseEnterOnce);
    //     };
    // }, []);

    const getWhatsappTemplateButtonType = async () => {
        const res = await getWhatsappTemplateButtonTypeApiCall();
        if(!(res.status)){
            // handleError(res);
        }
    }

    const getWhatsappTemplateHeaderTypes = async () => {
        const res = await getWhatsappTemplateHeaderTypesApiCall();
        if(!(res.status)){
            // handleError(res);
        }
    }

    const getWhatsappTemplateDynamicVarTypes = async () => {
        const res = await getWhatsappTemplateDynamicVarTypesApiCall();
        if(!res.status){
            // handleError(res);
        }
    }

    const getWhatsappTemplateTypes = async () => {
        const res = await getWhatsappTemplateTypesApiCall();
        if(!(res.status)){
            // handleError(res);
        }
    }

    const getInteractiveTypeList = async () => {
        const res = await getInteractiveTypeListApiCall();
        if (!(res?.status)) {
            handleError(res);
        }
    }

    const uploadFileHandler = async (e: any) => {
        const fileList = e?.target?.files;
        if (!fileList || fileList.length === 0) return;
        const file: any = fileList[0];

        file.isUploading = true;
        setFiles([file]);

        const formData = new FormData();
        formData.append("file", file, file?.name);
        formData.append("chatbot_flag", "true");

        const config = {
            onUploadProgress: (progressEvent: any) => {
                const { loaded, total } = progressEvent;
                const percent = total ? Math.floor((loaded * 100) / total) : 0;
                setProgress((p: { percent: number; filename: string }) => ({ ...p, percent, filename: file?.name }));
            }
        };

        setFlagLoading((prev: any) => ({ ...prev, flagRoundLoader: true }));
        try {
            const res = await agent.uploadFile.post(formData, config);
            const success = res?.data?.status ?? res?.status;
            const responseData = res?.data?.data ?? res?.data;
            if (success && responseData?.file_path != null) {
                const fileType = (file?.name || "").split(".").pop();
                const fileTypeIconSrc = getFileTypeImgIcon(fileType) ?? "";
                const updatedFile = {
                    ...file,
                    name: file?.name,
                    size: file?.size,
                    isUploading: false,
                    file_data: responseData,
                    google_storage_id: responseData?.google_storage_id,
                    previewImageSrc: fileTypeIconSrc,
                };
                setFiles([updatedFile]);
                setIsFileUploaded(false);
                const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
                const headerDataList = latestPayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data ?? [];
                setCreateTemplatePayload({
                    ...latestPayload,
                    whatsapp_template_data: {
                        ...latestPayload?.whatsapp_template_data,
                        whatsapp_template_header: {
                            ...latestPayload?.whatsapp_template_data?.whatsapp_template_header,
                            whatsapp_template_header_data: headerDataList?.map((headerData: any) => ({
                                ...headerData,
                                whatsapp_template_header_data_value: responseData?.file_path,
                                whatsapp_template_header_data_google_storage_id: responseData?.google_storage_id ?? headerData?.whatsapp_template_header_data_google_storage_id,
                            }))
                        },
                    },
                });
            } else {
                handleError(res?.data ?? res);
                setFiles([]);
            }
        } catch (err) {
            console.error(err);
            showError("Something went wrong!")
            setFiles([]);
        } finally {
            setFlagLoading((prev: any) => ({ ...prev, flagRoundLoader: false }));
        }
    };

    const removeUploadedFile = async (storage_id: any, showSuccessMessage: boolean) => {
        await agent.removeUploadedFile.delete(storage_id).then(async (res) => {
            if (res.data.status) {
                if (showSuccessMessage) {
                    showSuccess("File Deleted Successfully")
                }
            } else {
                handleError(res.data);
            }
        }).catch((err) => {
            console.log(err);
        })
    };

    const removeFileHandler = async (storage_id: number) => {
        setFlagLoading({ ...flagLoading, flagRoundLoader: true });
        if (!(createTemplatePayload?.whatsapp_template_id) && !searchParams.get('copy_template')) {
            removeUploadedFile(storage_id, true);
        }
        if (files) {
            setFiles(() => files?.filter((file: any) => file?.file_data?.google_storage_id !== storage_id));
        } else {
            setFiles([]);
        }
        const headerDataList = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data ?? [];
        const clearedHeaderData = headerDataList.length > 0
            ? headerDataList.map((headerData: any) => ({
                ...headerData,
                whatsapp_template_header_data_value: "",
                whatsapp_template_header_data_google_storage_id: 0,
              }))
            : [{ whatsapp_template_header_data_value: "", whatsapp_template_header_data_id: 0, whatsapp_template_header_data_google_storage_id: 0, whatsapp_template_header_variable_data: [] }];
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_header: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header,
                    whatsapp_template_header_data: clearedHeaderData,
                },
            },
        });
        setFlagLoading({ ...flagLoading, flagRoundLoader: false });
    };

    const handleChangeText = (event: any, type: any) => {
        if (type === "format") {
            if (event.target.value.length <= maxChars) {
                setText(event.target.value);
            }
        }
        else if (type === "footer") {
            if (event.target.value.length <= maxFooterChars)
                setFooterText(event.target.value)
        }
        else if (type === "carouselheader") {
            if (event.target.value.length <= CAROUSEL_MAX_CHARS?.HEADER_TEXT)
                setCarouselHeaderText(event.target.value)
        }
    };

    const updateTemplateTextContent = (isTypeHeader: boolean, value: string, variableData?: any[]) => {
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                ...(isTypeHeader
                    ? {
                        whatsapp_template_header: {
                            ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header,
                            whatsapp_template_header_data:
                            createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.map((headerData: any) => ({
                                    ...headerData,
                                    whatsapp_template_header_data_value: value,
                                    ...(variableData && 
                                        {
                                            whatsapp_template_header_variable_data: variableData
                                        }
                                    )
                                }))
                        }
                    }
                    : {
                        whatsapp_template_body: {
                            ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body,
                            whatsapp_template_body_value: value,
                            ...(variableData && 
                                {
                                    whatsapp_template_body_variable_data: variableData
                                }
                            )
                        }
                    }
                )
            }
        });
    };

    const handleAddBodyVariableManually = (value: string, type: string) => {
        const isTypeHeader = type.toLowerCase() === "header";
        const flagNamedParam = createTemplatePayload?.flag_named_parameter;

        let variableData = isTypeHeader
            ? [...(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data[0]?.whatsapp_template_header_variable_data || [])]
            : [...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_variable_data];

        updateTemplateTextContent(isTypeHeader, value);

        // Named parameter
        if (flagNamedParam) {
            const match = value.match(/{{([^}]+)}}$/);
            if (!match) return;

            const variableKey = `{{${match[1]}}}`;

            if (!variableData?.find(variable => variable?.whatsapp_template_dynamic_variable_key === variableKey)) {
                variableData.push({
                    whatsapp_template_dynamic_variable_key: variableKey,
                    whatsapp_template_dynamic_variable_sample_value: ""
                });
            }

            updateTemplateTextContent(isTypeHeader, value, variableData);
        } else {
            // Numeric parameter
            const nextIndex = isTypeHeader ? 1 : variableData.length + 1;
            const replacementString = `{{${nextIndex}}}`;
            const newValue = isTypeHeader && variableData.length !== 0
                ? value
                : value.slice(0, -2) + replacementString;
    
            if (!isTypeHeader || variableData.length === 0) {
                variableData.push({
                    whatsapp_template_dynamic_variable_key: replacementString,
                    whatsapp_template_dynamic_variable_sample_value: ""
                });
            }
    
            updateTemplateTextContent(isTypeHeader, newValue, variableData);
        }
    };

    const removeVariableData = (value: string, type: string) => {
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const isTypeHeader = type.toLowerCase() === "header";
        const flagNamedParam = latestPayload?.flag_named_parameter;

        const regex = getVariableRegex(flagNamedParam)
        const matchedVariables = value.match(regex) || [];
        const uniqueVariables = Array.from(new Set(matchedVariables));

        let normalizedTextValue = value;

        // 🔹 Numeric renumbering (BODY ONLY)
        if (!flagNamedParam && !isTypeHeader) {
            uniqueVariables?.forEach((match: any, index: number) => {
                const currentIndex = Number(match.replace(/[{}]/g, ""));
                const expectedIndex = index + 1;

                if (currentIndex !== expectedIndex) {
                    normalizedTextValue = normalizedTextValue?.replaceAll(`{{${currentIndex}}}`, `{{${expectedIndex}}}`);
                }
            });
        }

        const variableData = isTypeHeader
            ? latestPayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data[0]?.whatsapp_template_header_variable_data || []
            : latestPayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_variable_data;

        // const mappedVariableData = uniqueVariables?.map((key: any, index: number) => ({
        //     whatsapp_template_dynamic_variable_key: flagNamedParam ? key : `{{${index + 1}}}`,
        //     whatsapp_template_dynamic_variable_sample_value:
        //         variableData?.find((v: any) => v?.whatsapp_template_dynamic_variable_key === key)?.whatsapp_template_dynamic_variable_sample_value ?? ""
        // }));

        const mappedVariableData = uniqueVariables?.map((key, index) => {
            const isVariableExist = variableData?.find((v: any) => v.whatsapp_template_dynamic_variable_key === key);
            if(isVariableExist){
                return isVariableExist;
            }
            return {
                whatsapp_template_dynamic_variable_key: flagNamedParam ? key : `{{${index + 1}}}`,
                whatsapp_template_dynamic_variable_sample_value: ''
            }
        })

        return {
            textAreaValue: normalizedTextValue,
            variableData: uniqueVariables?.length > 0
                ? (isTypeHeader ? [mappedVariableData[0]] : mappedVariableData)
                : []
        };
    };

    const handleAddField = (type: string) => {
        const flagNamedParams = createTemplatePayload?.flag_named_parameter; 
        const isTypeHeader = type.toLowerCase() === "header";
        
        let fields: any = !isTypeHeader ?
            createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_variable_data
            :
            createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data[0]?.whatsapp_template_header_variable_data
        
            const newFieldId = flagNamedParams ? '' : fields.length + 1;

        let fieldObj = {
            "whatsapp_template_dynamic_variable_key": `{{${newFieldId}}}`,
            "whatsapp_template_dynamic_variable_sample_value": ""
        }
        if(!flagNamedParams){
            fields.push(fieldObj)
        }
        if (!isTypeHeader) {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_body: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body,
                        whatsapp_template_body_value: insertVariable(String(newFieldId) ?? '', 'body', createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_value ?? ''),
                        whatsapp_template_body_variable_data: fields
                    }
                }
            });
        } else {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_header: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header,
                        whatsapp_template_header_data: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.map((variable: any) => {
                            return {
                                ...variable,
                                whatsapp_template_header_data_value: insertVariable(String(newFieldId) ?? '', 'header', createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_value ?? ''),
                                whatsapp_template_header_variable_data: fields
                            }
                        })
                    }
                }
            })
        }
    };

    const handleChange = (id: any, event: any, type: string) => {
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const bodyVariableData = latestPayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_variable_data;
        const headerVariableData = latestPayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data;
        const fields = type.toLowerCase() === "body" ? (bodyVariableData ?? []) : (headerVariableData ?? []);
        const newFields = fields.map((field: any) => {
            if (field.whatsapp_template_dynamic_variable_key === id) {
                return { ...field, whatsapp_template_dynamic_variable_sample_value: event.target.value };
            }
            return field;
        });
        if (type.toLowerCase() === "body") {
            const updatedPayload: createWhatsappTemplatePayloadForTemplateLibrary = {
                ...latestPayload,
                whatsapp_template_data: {
                    ...latestPayload?.whatsapp_template_data,
                    whatsapp_template_body: {
                        ...latestPayload?.whatsapp_template_data?.whatsapp_template_body,
                        whatsapp_template_body_variable_data: newFields
                    }
                }
            };
            setCreateTemplatePayload(updatedPayload);
        } else if (type.toLowerCase() === "header") {
            const updatedPayload: createWhatsappTemplatePayloadForTemplateLibrary = {
                ...latestPayload,
                whatsapp_template_data: {
                    ...latestPayload?.whatsapp_template_data,
                    whatsapp_template_header: {
                        ...latestPayload?.whatsapp_template_data?.whatsapp_template_header,
                        whatsapp_template_header_data: (latestPayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data ?? []).map((variable: any) => ({
                            ...variable,
                            whatsapp_template_header_variable_data: newFields,
                        }))
                    }
                }
            };
            setCreateTemplatePayload(updatedPayload);
        }
    };

    const handleRemoveField = (id: any, type: string) => {
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const bodyVariableData = latestPayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_variable_data;
        const headerVariableData = latestPayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data;
        const fields: any[] = type.toLowerCase() === "body" ? (bodyVariableData ?? []) : (headerVariableData ?? []);
        const newFields = fields?.filter((field: any) => field?.whatsapp_template_dynamic_variable_key !== id);
        if (type.toLowerCase() === "body") {
            const updatedPayload: createWhatsappTemplatePayloadForTemplateLibrary = {
                ...latestPayload,
                whatsapp_template_data: {
                    ...latestPayload?.whatsapp_template_data,
                    whatsapp_template_body: {
                        ...latestPayload?.whatsapp_template_data?.whatsapp_template_body,
                        whatsapp_template_body_value: `${(latestPayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_value ?? '').replaceAll(`${id}`, '').trim()}`,
                        whatsapp_template_body_variable_data: newFields
                    }
                }
            };
            setCreateTemplatePayload(updatedPayload);
        } else if (type.toLowerCase() === "header") {
            const headerDataList = latestPayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data ?? [];
            const updatedPayload: createWhatsappTemplatePayloadForTemplateLibrary = {
                ...latestPayload,
                whatsapp_template_data: {
                    ...latestPayload?.whatsapp_template_data,
                    whatsapp_template_header: {
                        ...latestPayload?.whatsapp_template_data?.whatsapp_template_header,
                        whatsapp_template_header_data: headerDataList.map((variable: any, ind: number) => ({
                            ...variable,
                            whatsapp_template_header_data_value: `${(headerDataList[ind]?.whatsapp_template_header_data_value ?? '').replaceAll(`${id}`, '').trim()}`,
                            whatsapp_template_header_variable_data: newFields,
                        }))
                    }
                }
            };
            setCreateTemplatePayload(updatedPayload);
        }
    };

    const onChangeOfHeader = (value: string) => {
        setFiles([])
        if (value.toLowerCase() === "none" && createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_type_name?.toLowerCase() !== "none") {
            setExistingHeaderTemplateVariableData(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data
                ? createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data
                : null
            );
        }
        const updatedPayload: createWhatsappTemplatePayloadForTemplateLibrary = {
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_header: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header,
                    whatsapp_template_header_data: [{
                        "whatsapp_template_header_data_value": "",
                        "whatsapp_template_header_data_id": createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_id,
                        "whatsapp_template_header_data_google_storage_id": 0,
                        "whatsapp_template_header_variable_data": value.toLowerCase() === "location"
                            ? createEmptyLocationHeaderVariables()
                            : [],
                    }],
                    whatsapp_template_header_type_id: whatsappTemplateHeaderTypes?.find((hType: any) => hType?.whatsapp_template_header_type_name === value)?.whatsapp_template_header_type_id || 0,
                    whatsapp_template_header_type_name: value
                },
            },
        };
        setCreateTemplatePayload(updatedPayload);
    };

    const checkTypeOfVariableValidation = (type: string) => {
        const templateHeader = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header;
        const headerVariableData = templateHeader?.whatsapp_template_header_type_name?.toLowerCase() === 'text' ? templateHeader?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data || [] : [];
        const bodyVariableData = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_variable_data;
        
        const flagNamedParams = createTemplatePayload?.flag_named_parameter;

        switch(type?.toLowerCase()){
            case 'header':
                return validateVariableKeys(headerVariableData, flagNamedParams);
            case 'body':
                return validateVariableKeys(bodyVariableData, flagNamedParams);
            case 'both':
                return validateVariableKeys([...headerVariableData, ...bodyVariableData], flagNamedParams);
            default:
                return false;
        }
    }

    // Buttons

    const calculateMenuPosition = (anchorElement: HTMLElement) => {
        if (!anchorElement) return 'bottom';

        const rect = anchorElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        const menuItemHeight = 48;
        const menuItemCount = whatsappTemplateButtonGenericData?.reduce((total: number, btnOption: any) => {
            return total + 1 + btnOption.whatsapp_template_button_sub_type_data.length;
        }, 0) || 0;
        const menuHeight = Math.min(menuItemCount * menuItemHeight + 100, 400);

        const spacing = 8;

        const spaceBelow = viewportHeight - rect.bottom - spacing;
        const spaceAbove = rect.top - spacing;

        if (spaceBelow < menuHeight && spaceAbove >= menuHeight) {
            return 'top';
        }

        if (spaceAbove < menuHeight && spaceBelow >= menuHeight) {
            return 'bottom';
        }

        if (spaceBelow >= spaceAbove) {
            return 'bottom';
        } else {
            return 'top';
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        const position = calculateMenuPosition(event.currentTarget);
        setMenuPosition(position);

        setAnchorEl(event.currentTarget);
    };

    const checkDisableAddButton = (): boolean => {
        const totalButtons = getTotalButtonsForBasicTemplate(createTemplatePayload);
        return totalButtons >= getMaxButtonsCount(createTemplatePayload, interactiveTypeList ?? [], whatsappTemplateTypes ?? []);
    };

    const checkCountOfButtons = (btnId: number, subTypeID: number, maximumCount: number, flagCarousel?: boolean): boolean => {
        if (flagCarousel) {
            if (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[0]?.whatsapp_template_button?.flatMap((btn: any) => btn?.whatsapp_template_button_data)?.filter((btn: any) => btn?.whatsapp_template_button_sub_type_id === subTypeID).length === maximumCount) {
                return true;
            }
            else {
                return false;
            }
        }
        else {
            if (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button?.flatMap((btn: any) => btn?.whatsapp_template_button_data)?.filter((btn: any) => btn?.whatsapp_template_button_sub_type_id === subTypeID).length === maximumCount) {
                return true;
            }
            else {
                return false;
            }
        }
    }

    const getMenuDisableFlag = (btnId: number, subType: any, flagCarousel?: boolean) => {
        
        let subTypeID = subType.whatsapp_template_button_sub_type_id
        const btnSubTypeName = subType?.whatsapp_template_button_sub_type_name?.toLowerCase();
        
        if (subType?.whatsapp_template_button_sub_type_flag_active) {
            
            const shareContactInfoName = BTN_SUB_TYPE_NAMES['SHARE_CONTACT_INFO']?.toLowerCase();
            const hasShareContactInfo = hasShareContactInfoButton(createTemplatePayload, flagCarousel);

            if (hasShareContactInfo && btnSubTypeName !== shareContactInfoName) {
                return true;
            }

            switch (btnSubTypeName) {
                // case 1: // Marketing opt-out
                case BTN_SUB_TYPE_NAMES['MARKETING_OPT_OUT']?.toLowerCase():
                    {
                        // Category: Marketing
                        if (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_category_id === getTemplateCategoryId(TEMPLATE_CATEGORY?.MARKETING, templateLibraryCategoryList ?? [])) {
                            return checkCountOfButtons(btnId, subTypeID, maximumAllowedButtons(btnSubTypeName, createTemplatePayload, interactiveTypeList), flagCarousel);
                        }
                        else {
                            return true
                        };
                    }
                
                // case 2: // Custom 
                case BTN_SUB_TYPE_NAMES['CUSTOM']?.toLowerCase():
                    if (createTemplatePayload?.whatsapp_template_flag_interactive_message) {
                        if (isInteractiveTypeMessageFlow(createTemplatePayload?.interactive_type_id, interactiveTypeList) 
                            || isInteractiveTypeMessageCtaUrl(createTemplatePayload?.interactive_type_id, interactiveTypeList)
                            || isInteractiveTypeMessageRequestContactInfo(createTemplatePayload?.interactive_type_id, interactiveTypeList)
                        ) {
                            return true;
                        }
                        
                        if (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button?.find((btn) => btn?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_id === getButtonTypeIdByName(whatsappTemplateButtonGenericData, BTN_SUB_TYPE_NAMES['VISIT_WEBSITE']))) { // Interactive message, only supports either custom button or visit website.
                            return true;
                        }
                    }
                    return false;
                
                default:
                    return checkCountOfButtons(btnId, subTypeID, maximumAllowedButtons(btnSubTypeName, createTemplatePayload, interactiveTypeList), flagCarousel); // Interactive Message, only supports custom button.  
            }
        } else {
            return true;
        }
    }

    const handleClose = () => {
        setAnchorEl(null);
    };

    const isFlowButton = (buttonTypeId: number) => {
        const flowButton = whatsappTemplateButtonGenericData?.find((btn) => btn?.whatsapp_template_button_type_id === buttonTypeId);
        if (flowButton) {
            return flowButton?.whatsapp_template_button_type_name?.toLowerCase() === "flow";
        }
        return false;
    }

    const reOrderButtonFieldForCustomBtn = (selectedSubButton: getWhatsappTemplateButtonSubTypeReponse) => {
        const buttonDescriptionField = [];
        const payloadField = [];
        const otherFields = [];

        for (const item of selectedSubButton?.whatsapp_template_button_sub_type_field_data) {
            const fieldName = item.field_name.toLowerCase();
            switch (fieldName) {
                case "button description":
                    buttonDescriptionField?.push({
                        ...item,
                        whatsapp_template_button_sub_type_field_value: "",
                        whatsapp_template_button_sub_type_field_option_id: null,
                    });
                    break;
                case "payload":
                    payloadField?.push({
                        ...item,
                        whatsapp_template_button_sub_type_field_value: [],
                        whatsapp_template_button_sub_type_field_option_id: null,
                    });
                    break;
                default:
                    otherFields?.push({
                        ...item,
                        whatsapp_template_button_sub_type_field_value: "",
                        whatsapp_template_button_sub_type_field_option_id: null,
                    });
                    break;
            }
        }

        if (createTemplatePayload?.whatsapp_template_flag_interactive_message && getValidationsFromInteractiveType(createTemplatePayload?.interactive_type_id, interactiveTypeList)?.show_btn_description_field) {
            const reOrderedArray = [...otherFields, ...buttonDescriptionField, ...payloadField];
            return reOrderedArray;
        } else {
            return [...otherFields, ...payloadField];
        }
    }

    const getExistingActionButtonId = (buttonTypeId: number) => {
        let existingButton = existingTemplateData?.whatsapp_template_data?.whatsapp_template_button?.find((button) => Number(button?.whatsapp_template_button_type_id) === buttonTypeId)
        if (existingButton) {
            let buttonId = existingButton?.whatsapp_template_button_id
            return buttonId
        } else {
            return null
        }
    }

    const getExistingActionButtonDataId = (buttonTypeId: number, subTypeId: number, index?: number) => {
        let existingButton = existingTemplateData?.whatsapp_template_data?.whatsapp_template_button?.find((button) => Number(button?.whatsapp_template_button_type_id) === buttonTypeId)
        if (existingButton) {
            let subButtonData = existingButton?.whatsapp_template_button_data?.filter((subBtn) => subBtn?.whatsapp_template_button_sub_type_id === subTypeId)
            if (subButtonData && subButtonData.length > 0)
                var currentSubBtnData = [];
            let currentButtonData = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button?.find((btn) => Number(btn?.whatsapp_template_button_type_id) === buttonTypeId)
            let subBtnId = null
            if (index !== undefined) {
                let subBtndata = subButtonData[index]
                if (subBtndata && subBtndata?.whatsapp_template_button_data_id) {
                    subBtnId = subButtonData[index]?.whatsapp_template_button_data_id
                } else {
                    subBtnId = subButtonData[0]?.whatsapp_template_button_data_id ?? null
                }
            } else {
                if (currentButtonData) {
                    currentSubBtnData = currentButtonData?.whatsapp_template_button_data?.filter((subBtn) => subBtn?.whatsapp_template_button_sub_type_id === subTypeId)
                    let subBtndata = subButtonData?.[currentSubBtnData?.length]
                    if (subBtndata) {
                        subBtnId = subButtonData?.[currentSubBtnData?.length]?.whatsapp_template_button_data_id
                    }
                } else {
                    subBtnId = subButtonData[0]?.whatsapp_template_button_data_id
                }
            }
            return subBtnId
        } else {
            return null
        }
    }

    const getNewButtonType = (buttonTypeId: number, subTypeId: number) => {
        const selectedSubButton = whatsappTemplateButtonGenericData?.find((btn: any) => btn.whatsapp_template_button_type_id === buttonTypeId)?.whatsapp_template_button_sub_type_data?.find((subType: any) => subType.whatsapp_template_button_sub_type_id === subTypeId);
        const isCustom = selectedSubButton?.whatsapp_template_button_sub_type_name?.toLowerCase() === "custom";
        const isVisitWebsiteButton = selectedSubButton?.whatsapp_template_button_sub_type_name?.toLowerCase() === "visit website";

        let reorderedFields = selectedSubButton?.whatsapp_template_button_sub_type_field_data?.map((field) => ({
            ...field,
            "whatsapp_template_button_sub_type_field_value":
                field?.field_name?.toLowerCase() === "payload"
                    ? []
                    : (isVisitWebsiteButton && field?.field_name?.toLowerCase() === "website url" ? "https://" : field?.field_static_value_flag ? field?.field_static_value : ""),
            "whatsapp_template_button_sub_type_field_option_id": null,
            ...(isVisitWebsiteButton && field?.field_name?.toLowerCase() === "url type" ? {
                "whatsapp_template_button_sub_type_field_value": "Static",
                "whatsapp_template_button_sub_type_field_option_id": 2,
            } : {})
        })) ?? [];

        if (selectedSubButton && isCustom) {
            reorderedFields = reOrderButtonFieldForCustomBtn(selectedSubButton);
        }
        const newButtonType = {
            "whatsapp_template_button_id": getExistingActionButtonId(buttonTypeId),
            "flag_allow_tracking": getDefaultValueOfFlagAllowTracking(selectedSubButton?.whatsapp_template_button_sub_type_name, whatsappTemplateTypes?.find((types) => types.whatsapp_template_type_id === createTemplatePayload?.whatsapp_template_type_id)?.name || ""),
            "whatsapp_template_button_type_id": buttonTypeId,
            "whatsapp_template_button_data": [
                {
                    "whatsapp_template_button_data_id": getExistingActionButtonDataId(buttonTypeId, subTypeId),
                    "whatsapp_template_button_sub_type_id": subTypeId,
                    "whatsapp_template_button_sub_type_name": selectedSubButton?.whatsapp_template_button_sub_type_name,
                    "whatsapp_template_button_sub_type_field_data": reorderedFields,
                }
            ]
        };
        return newButtonType;
    }

    const getFlowListApi = async () => {
        const res = await agent.getWAFlowList.get(searchParams.get('id') ?? "");
        if (res.status && res.data) {
            const published_flows = res?.data?.filter((flow: any) => flow?.whatsapp_flow_version_data?.find((version: any) => version?.whatsapp_flow_metadata_response?.whatsapp_flow_meta_status?.toLowerCase() === "published"));
            setPublishedFlowList(published_flows || []);
        } else {
            handleError(res);
        }
    }

    const addButtonWithSubTypeGrouping = (existingButtons: createWhatsappTemplateButton[], newButton: ReturnType<typeof getNewButtonType>, subTypeId: number) => {
        const lastIndexOfNewBtn = existingButtons?.map((btn: createWhatsappTemplateButton) => btn?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_id)?.lastIndexOf(subTypeId);
        const btnsBeforeNewBtn = existingButtons?.slice(0, lastIndexOfNewBtn + 1) ?? [];
        const btnsAfterNewBtn = existingButtons?.slice(lastIndexOfNewBtn + 1) ?? [];

        // If same subTypeId exists, insert after last occurance
        if(lastIndexOfNewBtn !== -1){
            // We need to group buttons that share the same sub-type ID; otherwise, Meta will return an error.
            return [
                ...btnsBeforeNewBtn, 
                newButton,
                ...btnsAfterNewBtn
            ];
        }

        // If no matching subTypeId, append at the end
        return [...existingButtons, newButton];
    }

    const handleAddActionsButton = (buttonTypeId: number, subTypeId: number) => {
        const btnType = getNewButtonType(buttonTypeId, subTypeId);
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        
        const isShareContactInfo = btnType?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_name?.toLowerCase()
            === BTN_SUB_TYPE_NAMES['SHARE_CONTACT_INFO']?.toLowerCase();

        let clearedHeaderAndFooter = {};
        
        if (isShareContactInfo) {
            const headerStorageId = latestPayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_google_storage_id ?? 0;
            if (headerStorageId) {
                removeUploadedFile(headerStorageId, false);
            }
            setFiles([]);
            clearedHeaderAndFooter = {
                whatsapp_template_header: getClearedTemplateHeader(),
                whatsapp_template_footer: getClearedTemplateFooter(latestPayload?.whatsapp_template_data?.whatsapp_template_footer),
            };
        }

        setCreateTemplatePayload({
            ...latestPayload,
            whatsapp_template_data: {
                ...latestPayload?.whatsapp_template_data,
                ...clearedHeaderAndFooter,
                whatsapp_template_button: addButtonWithSubTypeGrouping(latestPayload?.whatsapp_template_data?.whatsapp_template_button ?? [], btnType, subTypeId) as createWhatsappTemplateButton[]
            }
        });

        if (isFlowButton(buttonTypeId) && !publishedFlowList) {
            getFlowListApi();
        }
    };

    const renderCustomButtonField = (field: any, whatsappTemplateButtonIndex: any, action: any, whatsappTemplateButtonDataIndex: any) => {
        const otherFields = [];
        const btnDescriptionField = [];
        const payloadField = [];

        for (const item of field.whatsapp_template_button_sub_type_field_data) {
            const fieldName = item?.field_name?.toLowerCase();
            switch (fieldName) {
                case "button description":
                    btnDescriptionField?.push(item);
                    break;
                case "payload":
                    payloadField?.push(item);
                    break;
                default:
                    otherFields?.push(item);
                    break;
            }
        }

        if (createTemplatePayload?.whatsapp_template_flag_interactive_message && getValidationsFromInteractiveType(createTemplatePayload?.interactive_type_id, interactiveTypeList)?.show_btn_description_field) {
            return [...otherFields, ...btnDescriptionField, ...payloadField];
        }

        return [...otherFields, ...payloadField];
    }

    const autoCompleteValue = (genericData: any, btnTypeId: number, subTypeId: number) => {

        if (genericData?.field_static_value_flag) {
            return genericData?.field_static_value;
        }
        if (isFlowButton(btnTypeId)) {
            const flow = publishedFlowList?.find((flow: any) => String(flow?.whatsapp_flow_id) === String(genericData?.whatsapp_template_button_sub_type_field_value));
            return flow || null;
        }

        const returnVal = whatsappTemplateButtonGenericData?.find((btn) => btn?.whatsapp_template_button_type_id === btnTypeId)
        ?.whatsapp_template_button_sub_type_data?.find((subType) => subType?.whatsapp_template_button_sub_type_id === subTypeId)
        ?.whatsapp_template_button_sub_type_field_data?.find((field) => field?.whatsapp_template_button_sub_type_field_id === genericData?.whatsapp_template_button_sub_type_field_id)
        ?.field_option_data?.find((option) => option?.field_option_id === genericData?.whatsapp_template_button_sub_type_field_option_id) ?? null;

        return returnVal;
    }

    const getOptionsDataForAutoCompleteField = (btnTypeId: number, subTypeId: number, genericData: any) => {
        if (genericData?.field_name?.toLowerCase() === "flow") {
            return publishedFlowList || [];
        } else {
            const btn = whatsappTemplateButtonGenericData?.find((btn) => btn?.whatsapp_template_button_type_id === btnTypeId);
            if(btn){
                const subTypeData = btn?.whatsapp_template_button_sub_type_data?.find((subType) => subType?.whatsapp_template_button_sub_type_id === subTypeId);
                if(subTypeData){
                    const fieldData = subTypeData?.whatsapp_template_button_sub_type_field_data?.find((fieldData) => fieldData?.field_name === genericData?.field_name);
                    if(fieldData){
                        return fieldData?.field_option_data || [];
                    }
                }
            }
            return [];
        }
    }

    const setCarouselPayloadOnChangeOfGetInput = (whatsappTemplateButtonIndex: number, inputTypeName: string, field_name: string, value: any, btnTypeId: number, whatsappTemplateButtonDataIndex: number, subFieldIndex: number, cardIndex: number) => {
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        setCreateTemplatePayload({
            ...latestPayload,
            whatsapp_template_data: {
                ...latestPayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...latestPayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: latestPayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card.map((card: any, ind: number) => {
                        if (ind === cardIndex) {
                            return {
                                ...card,
                                whatsapp_template_button: card?.whatsapp_template_button?.map((btn: any, btnIndex: any) => {
                                    if (btnIndex === whatsappTemplateButtonIndex) {
                                        return {
                                            ...btn,
                                            whatsapp_template_button_data: btn?.whatsapp_template_button_data?.map((buttonData: any, buttonDataIndex: any) => {
                                                if (buttonDataIndex === whatsappTemplateButtonDataIndex) {
                                                    return {
                                                        ...buttonData,
                                                        whatsapp_template_button_sub_type_field_data: buttonData?.whatsapp_template_button_sub_type_field_data?.map((field: any, fieldIndex: any) => {
                                                            if (field?.whatsapp_template_button_sub_type_field_id === subFieldIndex) {
                                                                return {
                                                                    ...field,
                                                                    ...(inputTypeName === "Autocomplete" ?
                                                                        {
                                                                            whatsapp_template_button_sub_type_field_option_id: value ? value?.field_option_id : null,
                                                                        } :
                                                                        {
                                                                            whatsapp_template_button_sub_type_field_value: (field_name === "Phone number") ? value?.replace(/[^0-9_]/g, '') : value,
                                                                        }
                                                                    ),

                                                                };
                                                            }
                                                            // return { ...field, whatsapp_template_button_field_data_variable: [] };
                                                            return { ...field };
                                                        })
                                                    };
                                                }
                                                return buttonData;
                                            })
                                        };
                                    }
                                    return btn;
                                })
                            }
                        }
                        return card;
                    })
                }
            }
        });
    }

    const setPayloadOnChangeOfGetInput = (whatsappTemplateButtonIndex: number, inputTypeName: string, field_name: string, value: any, btnTypeId: number, whatsappTemplateButtonDataIndex: number, subFieldIndex: number) => {
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        setCreateTemplatePayload({
            ...latestPayload,
            whatsapp_template_data: {
                ...latestPayload?.whatsapp_template_data,
                whatsapp_template_button: latestPayload?.whatsapp_template_data?.whatsapp_template_button?.map((btn: any, btnIndex: any) => {
                    if (btnIndex === whatsappTemplateButtonIndex) {
                        return {
                            ...btn,
                            whatsapp_template_button_data: btn?.whatsapp_template_button_data?.map((buttonData: any, buttonDataIndex: any) => {
                                if (buttonDataIndex === whatsappTemplateButtonDataIndex) {
                                    return {
                                        ...buttonData,
                                        whatsapp_template_button_sub_type_field_data: buttonData?.whatsapp_template_button_sub_type_field_data?.map((field: any, fieldIndex: any) => {
                                            if (field?.whatsapp_template_button_sub_type_field_id === subFieldIndex) {
                                                return {
                                                    ...field,
                                                    ...(inputTypeName === "Autocomplete" ?
                                                        value ?
                                                            "whatsapp_flow_id" in value ?
                                                                {
                                                                    whatsapp_template_button_sub_type_field_value: value?.whatsapp_flow_id || null,
                                                                }
                                                                :
                                                                {
                                                                    whatsapp_template_button_sub_type_field_option_id: value ? value?.field_option_id : null,
                                                                }
                                                            :
                                                            {
                                                                whatsapp_template_button_sub_type_field_value: null,
                                                                whatsapp_template_button_sub_type_field_option_id: null,
                                                            }
                                                        :
                                                        {
                                                            whatsapp_template_button_sub_type_field_value: (field_name === "Phone number") ? value?.replace(/[^0-9_]/g, '') : value,
                                                        }
                                                    ),

                                                };
                                            }
                                            // return { ...field, whatsapp_template_button_field_data_variable: [] };
                                            return { ...field };
                                        })
                                    };
                                }
                                return buttonData;
                            })
                        };
                    }
                    return btn;
                })
            }
        });
    }

    const isValidWebsiteURLWithVariable = (url: string) => {
        const isValidURL = validator.isURL(url, { require_protocol: true });
        if (!isValidURL) return false;

        const variableAtEndPattern = /\{\{\d+\}\}$/;
        if (!variableAtEndPattern.test(url)) return false;

        // 3. Only one variable allowed — count all `{{...}}` patterns
        const allVariables = url?.match(/\{\{\d+\}\}/g) || [];
        if (allVariables.length !== 1) return false;

        return true;
    }

    const inValidIdRefCarousel = useRef<any[]>([
        {
            index: 0,
            inValids: []
        },
        {
            index: 1,
            inValids: []
        }
    ]);

    const handleValidWebsiteWithVariables = (website_value: string, field: string, index: number, isDynamicUrl: boolean, cardIndex?: number) => {
        if (cardIndex || cardIndex === 0) {
            let cardData = inValidIdRefCarousel?.current?.find((ids: any) => ids.index === cardIndex)
            if (field === "Website URL" && website_value?.trim()?.length > 0) {
                let temp = isValidWebsiteURLWithVariable(website_value);
                if (temp || !isDynamicUrl) {
                    if (cardData) {
                        if (cardData.inValids?.includes(index)) {
                            setCarouselInvalidId((prevCarousels: any) =>
                                prevCarousels?.map((carousel: any) => {
                                    if (carousel?.index === cardData?.index) {
                                        return {
                                            ...carousel,
                                            inValidIds: carousel?.inValidIds?.filter((ele: any) => ele !== index)
                                        }
                                    }
                                    return carousel
                                })
                            )
                            inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                                if (refC?.index === cardData?.index) {
                                    return {
                                        ...refC,
                                        inValids: refC?.inValids?.filter((ele: any) => ele !== index)
                                    }
                                }
                                return refC
                            })
                        }
                    }
                } else {
                    if (cardData) {
                        if (cardData?.inValids?.includes(index)) { }
                        else {
                            setCarouselInvalidId((prevCarousels: any) =>
                                prevCarousels?.map((carousel: any) => {
                                    if (carousel?.index === cardData?.index) {
                                        return {
                                            ...carousel,
                                            inValidIds: carousel?.inValidIds && carousel?.inValidIds?.includes(index) ? carousel?.inValidIds : [...carousel?.inValidIds, index]
                                        }
                                    }
                                    return carousel
                                })
                            )
                            inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                                if (refC?.index === cardData?.index) {
                                    return {
                                        ...refC,
                                        inValids: refC?.inValids && refC?.inValids?.includes(index) ? refC?.inValids : [...refC?.inValids, index]
                                    }
                                }
                                return refC
                            })
                        }
                    }
                }
            } else if (field === "Website URL" && website_value.trim().length === 0) {
                if (cardData) {
                    if (cardData?.inValids?.includes(index)) {
                        setCarouselInvalidId((prevCarousels: any) =>
                            prevCarousels?.map((carousel: any) => {
                                if (carousel?.index === cardData?.index) {
                                    return {
                                        ...carousel,
                                        inValidIds: carousel?.inValidIds?.filter((ele: any) => ele !== index)
                                    }
                                }
                                return carousel
                            })
                        )
                        inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                            if (refC?.index === cardData?.index) {
                                return {
                                    ...refC,
                                    inValids: refC?.inValids?.filter((ele: any) => ele !== index)
                                }
                            }
                            return refC
                        })
                    }
                }
            }
        }
        else {
            if (field === "Website URL" && website_value?.trim()?.length > 0) {
                let temp = isValidWebsiteURLWithVariable(website_value);
                if (temp || !isDynamicUrl) {
                    if (inValidIdRef.current?.includes(index)) {
                        setInvalidId(inValidIdRef.current?.filter((ele: any) => ele !== index))
                        inValidIdRef.current = inValidIdRef.current?.filter((ele: any) => ele !== index);
                    } else {
                    }
                } else {
                    if (inValidIdRef.current?.includes(index)) {
                    } else {
                        setInvalidId([...inValidId, index])
                        inValidIdRef.current = [...inValidIdRef.current, index];
                    }
                }
            } else if (field === "Website URL" && website_value.trim().length === 0) {
                if (inValidIdRef.current?.includes(index)) {
                    setInvalidId(inValidIdRef.current?.filter((ele: any) => ele !== index))
                    inValidIdRef.current = inValidIdRef.current?.filter((ele: any) => ele !== index);
                } else {
                }
            }
        }
    }

    const handleVisitWebsiteVariable = useCallback(
        debounce((value, field, whatsappTemplateButtonIndex, isDynamicUrl, flagCarousel?, cardIndex?) => {
            if (flagCarousel) {
                handleValidWebsiteWithVariables(value, field, whatsappTemplateButtonIndex, isDynamicUrl, cardIndex);
            } else {
                handleValidWebsiteWithVariables(value, field, whatsappTemplateButtonIndex, isDynamicUrl);
            }
        }, 1200), []
    );

    const handleSampleTextChange = (index: number, fieldIndex: number, value: string, isURL: boolean, isStaticUrl?: boolean) => {
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        
        setCreateTemplatePayload({
            ...latestPayload,
            whatsapp_template_data: {
                ...latestPayload?.whatsapp_template_data,
                whatsapp_template_button: latestPayload?.whatsapp_template_data?.whatsapp_template_button
                    ?.map((btn, btnIndex) =>
                        btnIndex === index
                            ? {
                                ...btn,
                                whatsapp_template_button_data: btn?.whatsapp_template_button_data?.map(
                                    (field, fieldIdx) =>
                                        fieldIdx === fieldIndex
                                            ? {
                                                ...field,
                                                whatsapp_template_button_sub_type_field_data:
                                                    field?.whatsapp_template_button_sub_type_field_data?.map(
                                                        (subType) =>
                                                            subType?.field_name?.toLowerCase() === "website url"
                                                                ? {
                                                                    ...subType,
                                                                    whatsapp_template_button_field_data_variable: isStaticUrl ? [] : [
                                                                        {
                                                                            whatsapp_template_dynamic_variable_key: "{{1}}",
                                                                            whatsapp_template_dynamic_variable_sample_value: value,
                                                                        },
                                                                    ],
                                                                }
                                                                : subType
                                                    ),
                                            }
                                            : field
                                ),
                            }
                            : btn
                    ),
            },
        });

        if (isURL) handleOnBlurOfDynamicVarOfBtn(value, "Website URL", index, fieldIndex, false);
    }

    const isVisitWebsiteSubType = (buttonTypeId: number, subTypeId: number) => {
        const selectedSubButton = whatsappTemplateButtonGenericData
            ?.find((btn: any) => btn?.whatsapp_template_button_type_id === buttonTypeId)
            ?.whatsapp_template_button_sub_type_data?.find((subType: any) => subType?.whatsapp_template_button_sub_type_id === subTypeId);
        return selectedSubButton?.whatsapp_template_button_sub_type_name?.toLowerCase() === "visit website";
    };

    const ensureHttpsPrefix = (urlValue: string) => {
        const value = (urlValue ?? "").trim();
        if (!value) return "https://";
        if (value.toLowerCase().startsWith("https://")) return value;
        if (value.toLowerCase().startsWith("http://")) return `https://${value.slice(7)}`;
        return `https://${value}`;
    };

    const getUpdatedFieldNameAndPlaceholderForListType = (genericData: any, btnTypeId: number, subTypeId: number, whatsapp_template_button_sub_type_field_data: any) => {

        const btn = whatsappTemplateButtonGenericData?.find((btn) => btn?.whatsapp_template_button_type_id === btnTypeId);
        
        if(btn && btn?.whatsapp_template_button_type_name.toLowerCase() === "call to action"){
            const subTypeData = btn?.whatsapp_template_button_sub_type_data?.find((subType) => subType?.whatsapp_template_button_sub_type_id === subTypeId);
            if(subTypeData && subTypeData?.whatsapp_template_button_sub_type_name.toLowerCase() === "visit website"){
                const selectedUrlType = whatsapp_template_button_sub_type_field_data?.find(
                    (subType: any) => subType?.field_name?.toLowerCase() === "url type"
                );

                const urlTypeId = selectedUrlType?.whatsapp_template_button_sub_type_field_option_id;
                const urlType = selectedUrlType?.field_option_data?.find((option: any) => option.field_option_id === urlTypeId)?.field_option_value
                    ?? selectedUrlType?.whatsapp_template_button_sub_type_field_value;

                if (genericData?.field_name?.toLowerCase() === "website url" && (urlType || "").toLowerCase() === "dynamic") {
                    return {
                        field_name: genericData?.field_name,
                        placeholder: `${genericData?.field_placeholder}/{{1}}`,
                    }
                }
            }
        }

        if (createTemplatePayload?.whatsapp_template_flag_interactive_message && isInteractiveTypeMessageList(createTemplatePayload?.interactive_type_id, interactiveTypeList)) {
            return {
                field_name: genericData?.field_name?.replace(/Button/g, "Row")?.replace(/Text/g, "Title"),
                placeholder: genericData?.field_placeholder?.replace(/Button/g, "Row"),
            }
        }
        return {
            field_name: genericData?.field_name,
            placeholder: genericData?.field_placeholder,
        }
    }

    const getUpdatedFieldCharacterLimit = (genericData: any) => {
        if (createTemplatePayload?.whatsapp_template_flag_interactive_message && (genericData?.field_name?.toLowerCase() === "button text" || genericData?.field_name?.toLowerCase() === "button description")) {
            if (genericData?.field_name?.toLowerCase() === "button text") {
                return getValidationsFromInteractiveType(createTemplatePayload?.interactive_type_id, interactiveTypeList)?.button_text_character_limit;
            } else {
                return getValidationsFromInteractiveType(createTemplatePayload?.interactive_type_id, interactiveTypeList)?.btn_description_character_limit;
            }
        } else {
            return genericData?.field_character_limit;
        }
    }

    const handleValidWebsite = (website_value: any, field: any, index: any, cardIndex?: number) => {
        if (cardIndex || cardIndex === 0) {
            let cardData = inValidIdRefCarousel?.current?.find((ids: any) => ids.index === cardIndex)
            if (field === "Website URL" && website_value?.trim()?.length > 0) {
                let temp = validator?.isURL(website_value, { require_protocol: true })
                if (temp) {
                    if (cardData) {
                        if (cardData?.inValids?.includes(index)) {
                            setCarouselInvalidId((prevCarousels: any) =>
                                prevCarousels?.map((carousel: any) => {
                                    if (carousel?.index === cardData?.index) {
                                        return {
                                            ...carousel,
                                            inValidIds: carousel?.inValidIds?.filter((ele: any) => ele !== index)
                                        }
                                    }
                                    return carousel
                                })
                            )
                            inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                                if (refC?.index === cardData?.index) {
                                    return {
                                        ...refC,
                                        inValids: refC?.inValids?.filter((ele: any) => ele !== index)
                                    }
                                }
                                return refC
                            })
                        }
                    }
                } else {
                    if (cardData) {
                        if (cardData?.inValids?.includes(index)) { }
                        else {
                            setCarouselInvalidId((prevCarousels: any) =>
                                prevCarousels?.map((carousel: any) => {
                                    if (carousel?.index === cardData?.index) {
                                        return {
                                            ...carousel,
                                            inValidIds: carousel?.inValidIds && carousel?.inValidIds?.includes(index) ? carousel?.inValidIds : [...carousel?.inValidIds, index]
                                        }
                                    }
                                    return carousel
                                })
                            )
                            inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                                if (refC?.index === cardData?.index) {
                                    return {
                                        ...refC,
                                        inValids: refC?.inValids && refC?.inValids?.includes(index) ? refC?.inValids : [...refC?.inValids, index]
                                    }
                                }
                                return refC
                            })
                        }
                    }
                }
            } else if (field === "Website URL" && website_value?.trim()?.length === 0) {
                if (cardData) {
                    if (cardData?.inValids?.includes(index)) {
                        setCarouselInvalidId((prevCarousels: any) =>
                            prevCarousels?.map((carousel: any) => {
                                if (carousel?.index === cardData?.index) {
                                    return {
                                        ...carousel,
                                        inValidIds: carousel?.inValidIds?.filter((ele: any) => ele !== index)
                                    }
                                }
                                return carousel
                            })
                        )
                        inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                            if (refC?.index === cardData?.index) {
                                return {
                                    ...refC,
                                    inValids: refC?.inValids?.filter((ele: any) => ele !== index)
                                }
                            }
                            return refC
                        })
                    }
                }
            }
        }
        else {
            if (field === "Website URL" && website_value?.trim()?.length > 0) {
                let temp = validator.isURL(website_value, { require_protocol: true })
                if (temp) {
                    if (inValidIdRef.current?.includes(index)) {
                        setInvalidId(inValidIdRef.current?.filter((ele: any) => ele !== index))
                        inValidIdRef.current = inValidIdRef.current?.filter((ele: any) => ele !== index);
                    } else {
                    }
                } else {
                    if (inValidIdRef.current?.includes(index)) {
                    } else {
                        setInvalidId([...inValidId, index])
                        inValidIdRef.current = [...inValidIdRef.current, index];
                    }
                }
            } else if (field === "Website URL" && website_value?.trim()?.length === 0) {
                if (inValidIdRef.current.includes(index)) {
                    setInvalidId(inValidIdRef.current.filter((ele: any) => ele !== index))
                    inValidIdRef.current = inValidIdRef.current.filter((ele: any) => ele !== index);
                } else {
                }
            }
        }
    }

    const handleOnBlurOfDynamicVarOfBtn = useCallback(
        debounce((value, field, whatsappTemplateButtonIndex, whatsappTemplateButtonDataIndex, isURL, flagCarousel?, cardIndex?) => {
            if (flagCarousel) {
                if (!isURL) {
                    handleValidWebsite(value, field, String(whatsappTemplateButtonIndex), cardIndex);
                } else {
                    handleValidWebsite(value, field, whatsappTemplateButtonIndex, cardIndex);
                }
            }
            else {
                if (!isURL) {
                    handleValidWebsite(value, field, String(whatsappTemplateButtonIndex));
                } else {
                    handleValidWebsite(value, field, whatsappTemplateButtonIndex);
                }
            }
        }, 1200), []
    );

    const getNewPayloadObjectWithUpdatedPayloadKeyIdForCarousel = (field: any) => {
        const existedPayloads = Array.isArray(field?.whatsapp_template_button_sub_type_field_value) ?
            field?.whatsapp_template_button_sub_type_field_value?.map((subType: any, ind: number) => {
                return {
                    ...subType,
                    payload_key_id: ind,
                }
            })
            : [];

        return [...existedPayloads,
        {
            payload_key_id: field?.whatsapp_template_button_field_data_id ? field?.whatsapp_template_button_field_data_id
                : field?.whatsapp_template_button_sub_type_field_value?.length || 0, payload_key: "", payload_key_value: ""
        }
        ]
    }

    const handleAddPayloadDataCarousel = (btnTypeId: number, finalIndex: number, index: number, cardIndex: number, whatsappTemplateButtonIndex: number) => {
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: createTemplatePayload.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card.map((card: any, ind: number) => {
                        return {
                            ...card,
                            whatsapp_template_button: card?.whatsapp_template_button?.map((btn: any, btnIndex: any) => {
                                if (btn?.whatsapp_template_button_type_id === btnTypeId && btnIndex === whatsappTemplateButtonIndex) {
                                    return {
                                        ...btn,
                                        whatsapp_template_button_data: btn?.whatsapp_template_button_data?.map((buttonData: any, buttonDataIndex: any) => {
                                            if (buttonDataIndex === finalIndex) {
                                                return {
                                                    ...buttonData,
                                                    whatsapp_template_button_sub_type_field_data: buttonData?.whatsapp_template_button_sub_type_field_data?.map((field: any, fieldIndex: any) => {
                                                        if (field.whatsapp_template_button_sub_type_field_id === index) {
                                                            return {
                                                                ...field,
                                                                whatsapp_template_button_sub_type_field_value: getNewPayloadObjectWithUpdatedPayloadKeyIdForCarousel(field),
                                                            };
                                                        }
                                                        return { ...field };
                                                    })
                                                };
                                            }
                                            return buttonData;
                                        })
                                    };
                                }
                                return btn;
                            })
                        }
                    })
                }
            }
        });
    }

    const getNewPayloadObjectWithUpdatedPayloadKeyId = (field: any) => {
        const existedPayloads = field?.whatsapp_template_button_sub_type_field_value?.map((subType: any, ind: number) => {
            return {
                ...subType,
                payload_key_id: ind,
            }
        });
        return [...existedPayloads,
        {
            payload_key_id: field?.whatsapp_template_button_field_data_id ? field?.whatsapp_template_button_field_data_id
                : field?.whatsapp_template_button_sub_type_field_value?.length || 0, payload_key: "", payload_key_value: ""
        }
        ];
    }

    const handleAddPayloadData = (btnTypeId: number, finalIndex: number, index: number, whatsappTemplateButtonIndex: number) => {
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_button: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button?.map((btn: any, btnIndex: any) => {
                    if (btn?.whatsapp_template_button_type_id === btnTypeId && btnIndex === whatsappTemplateButtonIndex) {
                        return {
                            ...btn,
                            whatsapp_template_button_data: btn?.whatsapp_template_button_data?.map((buttonData: any, buttonDataIndex: any) => {
                                if (buttonDataIndex === finalIndex) {
                                    return {
                                        ...buttonData,
                                        whatsapp_template_button_sub_type_field_data: buttonData?.whatsapp_template_button_sub_type_field_data?.map((field: any, fieldIndex: any) => {
                                            if (field.whatsapp_template_button_sub_type_field_id === index) {
                                                return {
                                                    ...field,
                                                    whatsapp_template_button_sub_type_field_value: getNewPayloadObjectWithUpdatedPayloadKeyId(field),
                                                };
                                            }
                                            return { ...field };
                                        })
                                    };
                                }
                                return buttonData;
                            })
                        };
                    }
                    return btn;
                })
            }
        });
    }

    const checkDisableInputComponent = (genericData: any) => {
        const editableButtonFields = [
            TEMPLATE_BUTTON_FIELDS["BUTTON_TEXT"]?.toLowerCase(),
            TEMPLATE_BUTTON_FIELDS["BUTTON_DESCRIPTION"]?.toLowerCase()
        ]

        if(isAuthenticationTemplate) {
            return true;
        }

        if(editableButtonFields?.includes(genericData?.field_name?.toLowerCase()) && isBasicTemplate(whatsappTemplateTypes, createTemplatePayload?.whatsapp_template_type_id) && canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draft_id)){
            return false;
        } else if(createTemplatePayload.whatsapp_template_id){
            return true;
        } else {
            return genericData.field_static_value_flag;
        }
    }
    
    const getInput = (whatsappTemplateButtonIndex: number, inputTypeName: string, genericData: any, btnTypeId: number, whatsappTemplateButtonDataIndex: number, subFieldIndex: number, subTypeId: number, whatsapp_template_button_sub_type_field_data: any, flagCarousel?: boolean, cardIndex?: number, isPreview?: boolean) => {
        switch (inputTypeName) {
            case "Autocomplete":
                return (
                    <Grid item xs={12} sx={{ width: "100%" }}>
                        <Grid container direction={"row"} alignItems={"center"} gap={0} flexWrap={"nowrap"}>
                            <Grid item xs={3.5}>
                                <Grid container gap={"5px"} alignItems={"center"} flexWrap={"nowrap"}>
                                    <InputLabel className={`${formStyles.whatsappTemplateFormButtons__typeOfActionLabel}`}>
                                        {genericData?.field_name}
                                        {!genericData?.field_static_value_flag ?
                                            <Required />
                                            :
                                            <TouchTooltip title={DEFAULT_BUTTON_TEXT_TOOLTIP}>
                                                <InfoOutlinedIcon className={formStyles.whatsappTemplateFormButtons__typeOfActionLabelInfoIcon} />
                                            </TouchTooltip>
                                        }
                                    </InputLabel>
                                    {(isFlowButton(btnTypeId) && autoCompleteValue(genericData, btnTypeId, subTypeId)) &&
                                        <TouchTooltip title="View Flow in detail">
                                            <OpenInNewOutlinedIcon
                                                className={globalStyles.clickable}
                                                sx={isPreview ? { height: "14px", width: "14px" } : { height: "16px", width: "16px" }}
                                                onClick={() => {
                                                    window.open(`/channels/viewintegration/whatsapp/flowDetails?id=${searchParams.get('id') || ""}&flow_id=${autoCompleteValue(genericData, btnTypeId, subTypeId)?.whatsapp_flow_id}`, "_blank");
                                                }}
                                            />
                                        </TouchTooltip>
                                    }
                                </Grid>
                            </Grid>
                            <Grid item xs={8.5}>
                                <Autocomplete
                                    // disabled={createTemplatePayload.whatsapp_template_id ? true : genericData.field_static_value_flag}
                                    disabled={checkDisableInputComponent(genericData)}
                                    fullWidth
                                    size={isPreview ? "small" : "medium"}
                                    disablePortal={!isPreview}
                                    className={isPreview ? styles.callToActionAutocompletePreview : styles.callToActionAutocomplete}
                                    sx={
                                        isPreview
                                        ? {
                                            "& .MuiInputBase-input": {fontSize: "12px", padding: "4px 8px"},
                                            "& .MuiAutocomplete-endAdornment": {right: 4},
                                            "& .MuiAutocomplete-endAdornment .MuiIconButton-root, & .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {width: 20, height: 20, padding: 3, borderRadius: "50%"},
                                            "& .MuiAutocomplete-endAdornment .MuiIconButton-root:hover, & .MuiAutocomplete-popupIndicator:hover, & .MuiAutocomplete-clearIndicator:hover": {borderRadius: "50%"},
                                            "& .MuiAutocomplete-clearIndicator .MuiSvgIcon-root": {fontSize: 14},
                                        }
                                        : undefined
                                    }
                                    slotProps={{
                                        paper: {
                                            sx: isPreview
                                            ? {
                                                "& .MuiAutocomplete-option": {fontSize: "12px", padding: "4px 8px"}
                                            }
                                            : {
                                                "& .MuiAutocomplete-option": {fontSize: "13px"}
                                            },
                                        },
                                    }}
                                    getOptionLabel={(option: any) => option?.field_option_value || option?.whatsapp_flow_name}
                                    options={getOptionsDataForAutoCompleteField(btnTypeId, subTypeId, genericData)}
                                    value={autoCompleteValue(genericData, btnTypeId, subTypeId)}
                                    isOptionEqualToValue={(option: any, value: any) => {
                                        if (!option || value == null) return option === value;
                                        if (typeof value === "string") {
                                            const label = option?.field_option_value || option?.whatsapp_flow_name;
                                            return String(label) === String(value);
                                        }
                                        const optionId = option.field_option_id ?? option.whatsapp_flow_id;
                                        const valueId = value?.field_option_id ?? value?.whatsapp_flow_id;
                                        if (optionId == null || valueId == null) return option === value;
                                        return String(optionId) === String(valueId);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={genericData?.field_placeholder}
                                            sx={
                                                !isPreview
                                                    ? {
                                                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                                    }
                                                    : undefined
                                            }
                                        />
                                    )}
                                    ListboxProps={isPreview ? { style: { maxHeight: 100 }} : {style: { maxHeight: 200 }}}
                                    onChange={(event, value) => {
                                        if (flagCarousel) {
                                            setCarouselPayloadOnChangeOfGetInput(whatsappTemplateButtonIndex, inputTypeName, "", value, btnTypeId, whatsappTemplateButtonDataIndex, genericData?.whatsapp_template_button_sub_type_field_id, cardIndex || 0);
                                        }
                                        else {
                                            setPayloadOnChangeOfGetInput(whatsappTemplateButtonIndex, inputTypeName, "", value, btnTypeId, whatsappTemplateButtonDataIndex, genericData?.whatsapp_template_button_sub_type_field_id);
                                        }

                                        const websiteUrl = whatsapp_template_button_sub_type_field_data?.find((subType: any) => {
                                            return subType?.field_name?.toLowerCase() === "website url";
                                        });

                                        if (websiteUrl) {
                                            const website_url = websiteUrl?.whatsapp_template_button_sub_type_field_value;
                                            const normalizedWebsiteUrl = isVisitWebsiteSubType(btnTypeId, subTypeId) ? ensureHttpsPrefix(website_url || "") : website_url;

                                            if (isVisitWebsiteSubType(btnTypeId, subTypeId)) {
                                                if (flagCarousel) {
                                                    setCarouselPayloadOnChangeOfGetInput(whatsappTemplateButtonIndex, "Textfield", "Website URL", normalizedWebsiteUrl, btnTypeId, whatsappTemplateButtonDataIndex, websiteUrl?.whatsapp_template_button_sub_type_field_id,cardIndex || 0);
                                                } else {
                                                    setPayloadOnChangeOfGetInput(whatsappTemplateButtonIndex, "Textfield", "Website URL", normalizedWebsiteUrl, btnTypeId, whatsappTemplateButtonDataIndex, websiteUrl?.whatsapp_template_button_sub_type_field_id);
                                                }
                                            }

                                            if (flagCarousel) {
                                                handleVisitWebsiteVariable(normalizedWebsiteUrl, "Website URL", whatsappTemplateButtonIndex, value?.field_option_value.toLowerCase() === "dynamic", true, cardIndex);
                                            } else {
                                                handleVisitWebsiteVariable(normalizedWebsiteUrl, "Website URL", whatsappTemplateButtonIndex, value?.field_option_value.toLowerCase() === "dynamic");
                                            }
                                        }

                                        if (genericData?.field_name?.toLowerCase() === "url type" && value) {
                                            if (value?.field_option_value.toLowerCase() === "dynamic") {
                                                handleSampleTextChange(whatsappTemplateButtonIndex, whatsappTemplateButtonDataIndex, "", false);
                                            } else if (value?.field_option_value.toLowerCase() === "static") {
                                                if (flagCarousel) {
                                                    let carouselData = carouselInvalidId?.find((ids: any) => ids.index === cardIndex)
                                                    if (carouselData) {
                                                        if (carouselData?.inValidIds?.includes(String(whatsappTemplateButtonIndex))) {
                                                            setCarouselInvalidId((prevCarousel: any) =>
                                                                prevCarousel?.map((carousel: any) => {
                                                                    if (carousel.index === cardIndex) {
                                                                        return {
                                                                            ...carousel,
                                                                            inValidIds: carousel?.inValidIds?.filter((ele: any) => ele !== String(whatsappTemplateButtonIndex))
                                                                        }
                                                                    }
                                                                    return carousel
                                                                })
                                                            )
                                                            inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                                                                if (refC?.index === cardIndex) {
                                                                    return {
                                                                        ...refC,
                                                                        inValids: refC?.inValids?.filter((ele: any) => ele !== String(whatsappTemplateButtonIndex))
                                                                    }
                                                                }
                                                                return refC
                                                            })
                                                        }
                                                    }
                                                }
                                                else {
                                                    if(isInvalidUrl((genericData?.whatsapp_template_button_sub_type_field_value) ?? '')) {
                                                        if(inValidId.includes(whatsappTemplateButtonIndex)) {
                                                            setInvalidId(inValidId?.filter((ele: any) => ele !== whatsappTemplateButtonIndex));
                                                            inValidIdRef.current = inValidIdRef.current?.filter((ele) => ele !== whatsappTemplateButtonIndex);
                                                        }
                                                    }
                                                    handleSampleTextChange(whatsappTemplateButtonIndex, whatsappTemplateButtonDataIndex, "", false, true);
                                                }
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>);
            case "Textfield": {
                const urlTypeField = whatsapp_template_button_sub_type_field_data?.find((subType: any) => subType?.field_name?.toLowerCase() === "url type");
                const selectedUrlType = urlTypeField?.field_option_data?.find((option: any) => option.field_option_id === urlTypeField?.whatsapp_template_button_sub_type_field_option_id);
                
                const isStaticUrlType = selectedUrlType?.field_option_value?.toLowerCase() === "static";
                const isWebsiteUrlField = genericData?.field_name?.toLowerCase() === "website url";
                const websiteUrlValue = (genericData?.whatsapp_template_button_sub_type_field_value as string) ?? "";
                
                const carouselInvalidIds = flagCarousel ? carouselInvalidId?.find((carousels: any) => carousels.index === cardIndex)?.inValidIds : null;
                
                const showWebsiteUrlError = isWebsiteUrlField && (
                    (!isStaticUrlType && !flagCarousel && inValidId?.includes(whatsappTemplateButtonIndex)) ||
                    (flagCarousel && carouselInvalidIds?.includes(whatsappTemplateButtonIndex)) ||
                    (isStaticUrlType && isInvalidUrl(websiteUrlValue))
                );

                const templateButtons = flagCarousel
                    ? (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[cardIndex ?? 0]?.whatsapp_template_button ?? [])
                    : (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button ?? []);
                
                const showDuplicateButtonTextError = genericData?.field_name?.toLowerCase() === TEMPLATE_BUTTON_FIELDS['BUTTON_TEXT']?.toLowerCase()
                    && hasDuplicateButtonText(templateButtons, whatsappTemplateButtonIndex, whatsappTemplateButtonDataIndex);

                return <Grid item xs={12} sx={{ width: "100%" }}>
                    <Grid container direction={"row"} alignItems={"center"} gap={0} flexWrap={"nowrap"}>
                        <Grid item xs={3.5}>
                            <InputLabel className={`${formStyles.whatsappTemplateFormButtons__typeOfActionLabel}`}>
                                {getUpdatedFieldNameAndPlaceholderForListType(genericData, btnTypeId, subTypeId, whatsapp_template_button_sub_type_field_data)?.field_name}
                                {genericData?.field_name?.toLowerCase() !== "button description" &&
                                    <>
                                        {!genericData?.field_static_value_flag ?
                                            <Required />
                                            :
                                            <TouchTooltip title={DEFAULT_BUTTON_TEXT_TOOLTIP}>
                                                <InfoOutlinedIcon className={formStyles.whatsappTemplateFormButtons__typeOfActionLabelInfoIcon} />
                                            </TouchTooltip>
                                        }
                                    </>
                                }
                            </InputLabel>
                        </Grid>
                        <Grid item xs={8.5}>
                            <TextField
                                // disabled={createTemplatePayload.whatsapp_template_id ? true : genericData.field_static_value_flag}
                                disabled={checkDisableInputComponent(genericData) || genericData?.field_static_value_flag}
                                id="outlined-basic"
                                variant="outlined"
                                fullWidth
                                className={styles.templateGlobalTextfield}

                                placeholder={getUpdatedFieldNameAndPlaceholderForListType(genericData, btnTypeId, subTypeId, whatsapp_template_button_sub_type_field_data)?.placeholder}
                                // helperText={genericData.whatsapp_template_button_sub_type_field_value ? `${genericData.whatsapp_template_button_sub_type_field_value.length}/${genericData.field_character_limit}` : ""}
                                size='medium'
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                    },
                                    border: genericData?.field_name === "Website URL" && ((inValidId.includes(whatsappTemplateButtonIndex) && !flagCarousel) || (isStaticUrlType && isInvalidUrl((genericData?.whatsapp_template_button_sub_type_field_value as string) ?? ''))) ? "1px solid red" : genericData?.field_name === "Website URL" && carouselInvalidId?.find((carousels: any) => carousels.index === cardIndex)?.inValidIds?.includes(whatsappTemplateButtonIndex) && flagCarousel ? "1px solid red" : showDuplicateButtonTextError ? "1px solid red" : ""
                                }}
                                FormHelperTextProps={{
                                    style: {
                                        textAlign: 'right',
                                        fontSize: '10px',
                                        fontWeight: '600'
                                    }
                                }}
                                inputProps={{ dir: 'auto' }}
                                InputProps={{
                                    endAdornment: (
                                        <span style={{ fontSize: '10px', fontWeight: '600', paddingLeft: "4px", paddingRight: "4px" }}>
                                            {genericData?.whatsapp_template_button_sub_type_field_value ? `${genericData?.whatsapp_template_button_sub_type_field_value?.length}/${getUpdatedFieldCharacterLimit(genericData)}` : ""}
                                        </span>
                                    ),
                                }}
                                onChange={(event) => {
                                    const nativeInputType = (event.nativeEvent as any)?.inputType ?? "";
                                    const isDeleteAction = typeof nativeInputType === "string" && nativeInputType.startsWith("delete");
                                    const updatedInputValue =
                                        genericData?.field_name?.toLowerCase() === "website url" && isVisitWebsiteSubType(btnTypeId, subTypeId)
                                            ? (isDeleteAction ? event.target.value : ensureHttpsPrefix(event.target.value))
                                            : event.target.value;

                                    if (updatedInputValue.length <= getUpdatedFieldCharacterLimit(genericData)) {
                                        if (flagCarousel) {
                                            setCarouselPayloadOnChangeOfGetInput(whatsappTemplateButtonIndex, inputTypeName, genericData.field_name, updatedInputValue, btnTypeId, whatsappTemplateButtonDataIndex, genericData.whatsapp_template_button_sub_type_field_id, cardIndex || 0)
                                        }
                                        else {
                                            setPayloadOnChangeOfGetInput(whatsappTemplateButtonIndex, inputTypeName, genericData.field_name, updatedInputValue, btnTypeId, whatsappTemplateButtonDataIndex, genericData.whatsapp_template_button_sub_type_field_id);
                                        }
                                    }
                                    if (genericData?.field_name?.toLowerCase() === "website url") {
                                        if (updatedInputValue.length === 0) {
                                            if (flagCarousel) {
                                                let carouselData = carouselInvalidId?.find((ids: any) => ids.index === cardIndex)
                                                if (carouselData) {
                                                    if (carouselData?.inValidIds?.includes(String(whatsappTemplateButtonIndex))) {
                                                        setCarouselInvalidId((prevCarousel: any) =>
                                                            prevCarousel?.map((carousel: any) => {
                                                                if (carousel.index === cardIndex) {
                                                                    return {
                                                                        ...carousel,
                                                                        inValidIds: carousel.inValidIds.filter((ele: any) => ele !== String(whatsappTemplateButtonIndex))
                                                                    }
                                                                }
                                                                return carousel
                                                            })
                                                        )
                                                        inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                                                            if (refC.index === cardIndex) {
                                                                return {
                                                                    ...refC,
                                                                    inValids: refC.inValids.filter((ele: any) => ele !== String(whatsappTemplateButtonIndex))
                                                                }
                                                            }
                                                            return refC
                                                        })
                                                    }
                                                }
                                            }
                                            else {
                                                if (inValidId?.includes(whatsappTemplateButtonIndex)) {
                                                    setInvalidId(inValidId?.filter((ele: any) => ele !== whatsappTemplateButtonIndex));
                                                    inValidIdRef.current = inValidIdRef.current?.filter((ele) => ele !== whatsappTemplateButtonIndex);
                                                }
                                            }
                                            // handleSampleTextChange(whatsappTemplateButtonIndex, whatsappTemplateButtonDataIndex, "", true);
                                        }
                                        let url_type = "";
                                        const urlType = whatsapp_template_button_sub_type_field_data?.find((subType: any) => {
                                            return subType?.field_name?.toLowerCase() === "url type";
                                        });
                                        if (urlType) {
                                            const selectedUrlType = urlType?.field_option_data?.find((option: any) => option.field_option_id === urlType?.whatsapp_template_button_sub_type_field_option_id);
                                            if (selectedUrlType) {
                                                url_type = selectedUrlType?.field_option_value;
                                            }
                                        }
                                        if (flagCarousel) {
                                            handleOnBlurOfDynamicVarOfBtn(updatedInputValue, genericData.field_name, whatsappTemplateButtonIndex, whatsappTemplateButtonDataIndex, true, true, cardIndex);
                                            if (url_type) {
                                                handleVisitWebsiteVariable(updatedInputValue, genericData.field_name, whatsappTemplateButtonIndex, url_type?.toLowerCase() === "dynamic", true, cardIndex);
                                            }
                                        }
                                        else {
                                            handleOnBlurOfDynamicVarOfBtn(updatedInputValue, genericData.field_name, whatsappTemplateButtonIndex, whatsappTemplateButtonDataIndex, true);
                                            if (url_type) {
                                                handleVisitWebsiteVariable(updatedInputValue, genericData.field_name, whatsappTemplateButtonIndex, url_type?.toLowerCase() === "dynamic");
                                            }
                                        }
                                    }
                                }}
                                // onBlur={() => handleValidWebsite(genericData.whatsapp_template_button_sub_type_field_value, genericData.field_name, whatsappTemplateButtonDataIndex)}
                                value={genericData?.field_static_value_flag ? genericData?.field_static_value : genericData?.whatsapp_template_button_sub_type_field_value ?? null}
                                autoComplete='off'
                            // helperText={genericData.field_name === "Website URL" && inValidId.includes(whatsappTemplateButtonDataIndex) &&<span>Please Enter Valid URL</span>}
                            />

                            {showWebsiteUrlError && 
                                <span className={formStyles.errorTextWithTopMargin}>
                                    Please Enter Valid URL
                                </span>
                            }

                            {showDuplicateButtonTextError &&
                                <span className={formStyles.errorTextWithTopMargin}>
                                    {DUPLICATE_BUTTON_TEXT_ERROR}
                                </span>
                            }

                            {/* {genericData.field_name === "Website URL" && !isStaticUrlType && inValidId?.includes(whatsappTemplateButtonIndex) && !flagCarousel && <span className={formStyles.errorTextWithTopMargin}>Please Enter Valid URL</span>}
                            {genericData.field_name === "Website URL" && (carouselInvalidId?.find((carousels: any) => carousels.index === cardIndex)?.inValidIds && carouselInvalidId?.find((carousels: any) => carousels.index === cardIndex)?.inValidIds?.includes(whatsappTemplateButtonIndex)) && flagCarousel && <span className={formStyles.errorTextWithTopMargin}>Please Enter Valid URL</span>}
                            {genericData.field_name === "Website URL" && isStaticUrlType && isInvalidUrl((genericData?.whatsapp_template_button_sub_type_field_value as string) ?? '') && <span className={formStyles.errorTextWithTopMargin}>Please Enter Valid URL</span>} */}
                        </Grid>
                    </Grid>
                </Grid>;
            }
            case "Object":
                if (isPreview) return null;
                return <Grid item xs={12} sx={{ width: "100%" }}>
                    <Grid container direction={"row"} alignItems={"center"} gap={0.5} flexWrap={"nowrap"}>
                        <Grid item xs={3.5}>
                            <InputLabel className={`${formStyles.whatsappTemplateFormButtons__typeOfActionLabel}`}>
                                {genericData?.field_name}
                                {!genericData?.field_static_value_flag ?
                                    // <Required />
                                    <></>
                                    :
                                    <TouchTooltip title={DEFAULT_BUTTON_TEXT_TOOLTIP}>
                                        <InfoOutlinedIcon className={formStyles.whatsappTemplateFormButtons__typeOfActionLabelInfoIcon} />
                                    </TouchTooltip>
                                }
                            </InputLabel>
                        </Grid>
                        <Grid item xs={8.5}>
                            <GlobalButton
                                buttonText={`Add ${genericData?.field_name}`}
                                className='borderedButtonStyle'
                                sx={{ padding: "5px 10px !important", minWidth: "110px !important", fontSize: "12px !important" }}
                                onClick={() => {
                                    if (flagCarousel) {
                                        handleAddPayloadDataCarousel(btnTypeId, whatsappTemplateButtonDataIndex, genericData?.whatsapp_template_button_sub_type_field_id, cardIndex || 0, whatsappTemplateButtonIndex)
                                    }
                                    else {
                                        handleAddPayloadData(btnTypeId, whatsappTemplateButtonDataIndex, genericData?.whatsapp_template_button_sub_type_field_id, whatsappTemplateButtonIndex)
                                    }
                                }}
                                disabled={createTemplatePayload?.whatsapp_template_id ? true : false}
                            />
                        </Grid>
                    </Grid>
                </Grid>
        }
    }

    const flagShowVariableTooltipForVisitWebsite = (subField: any, whatsapp_template_button_sub_type_field_data: any) => {
        if (subField?.field_name?.toLowerCase() === "website url") {
            const urlType = whatsapp_template_button_sub_type_field_data?.find((subType: any) => {
                return subType?.field_name?.toLowerCase() === "url type";
            });
            if (urlType) {
                const selectedUrlType = urlType?.field_option_data?.find((option: any) => option?.field_option_id === urlType?.whatsapp_template_button_sub_type_field_option_id);
                if (selectedUrlType) {
                    if (selectedUrlType?.field_option_value?.toLowerCase() === "dynamic") {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    const handleDeleteActionBtn = (buttonTypeId: number, subButtonId: number, whatsappTemplateButtonDataIndex: number, index: number) => {
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_button: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button?.filter((btn: any, ind: number) => ind !== index)
            }
        });
        if (inValidId?.includes(String(index + whatsappTemplateButtonDataIndex))) {
            setInvalidId(inValidId?.filter((ele: any) => ele !== String(index + whatsappTemplateButtonDataIndex)))
            inValidIdRef.current = inValidIdRef.current?.filter((ele: any) => ele !== String(index + whatsappTemplateButtonDataIndex));
        }
        if (inValidId?.includes(whatsappTemplateButtonDataIndex)) {
            setInvalidId(inValidId?.filter((ele: any) => ele !== whatsappTemplateButtonDataIndex));
            inValidIdRef.current = inValidIdRef.current?.filter((ele: any) => ele !== whatsappTemplateButtonDataIndex);
        } 

        if(isFlowButton(buttonTypeId)){
            setInvalidPayloadObjectId([]);
        }
    }

    const setOnChangeOfPayloadObject = (field_name: string, value: any, btnTypeId: number, finalIndex: number, payloadIndex: number, subTypeIndex: number, whatsappTemplateButtonIndex: number, flagRemove?: boolean) => {
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_button: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button?.map((btn: any, btnIndex: any) => {
                    if (btn.whatsapp_template_button_type_id === btnTypeId && btnIndex === whatsappTemplateButtonIndex) {
                        return {
                            ...btn,
                            whatsapp_template_button_data: btn?.whatsapp_template_button_data?.map((buttonData: any, buttonDataIndex: any) => {
                                if (buttonDataIndex === finalIndex) {
                                    return {
                                        ...buttonData,
                                        whatsapp_template_button_sub_type_field_data: buttonData?.whatsapp_template_button_sub_type_field_data?.map((field: any, fieldIndex: any) => {
                                            if (field?.whatsapp_template_button_sub_type_field_id === subTypeIndex) {
                                                return {
                                                    ...field,
                                                    whatsapp_template_button_sub_type_field_value: flagRemove ?
                                                        field?.whatsapp_template_button_sub_type_field_value?.filter((payload: any) =>
                                                            payload.payload_key_id ? payload.payload_key_id !== payloadIndex : payload.whatsapp_template_button_field_data_id !== payloadIndex
                                                        )
                                                        :
                                                        field?.whatsapp_template_button_sub_type_field_value?.map((payload: any) =>
                                                            payload?.payload_key_id ? payload?.payload_key_id === payloadIndex
                                                                ? { ...payload, [field_name]: value }
                                                                : payload
                                                                :
                                                                payload?.whatsapp_template_button_field_data_id === payloadIndex
                                                                    ? { ...payload, [field_name]: value }
                                                                    : payload
                                                        )
                                                };
                                            }
                                            return field;
                                        })
                                    };
                                }
                                return buttonData;
                            })
                        };
                    }
                    return btn;
                })
            }
        });
    }

    const checkForAddDynamicVar = (action: createWhatsappTemplateButton, field: createWhatsappTemplateButtonData): boolean => {
        const ctaBtnId = action?.whatsapp_template_button_type_id;
        const subTypeId = field?.whatsapp_template_button_sub_type_id;

        const urlType = field?.whatsapp_template_button_sub_type_field_data?.find((subField: createWhatsappTemplateButtonSubFieldData) => {
            return (subField?.field_name?.toLowerCase() === "url type");
        });
        const websiteURL = field?.whatsapp_template_button_sub_type_field_data?.find((subField: createWhatsappTemplateButtonSubFieldData) => {
            return (subField?.field_name?.toLowerCase() === "website url");
        });
        if (urlType && websiteURL) {
            if (ctaBtnId === 2 && subTypeId === 3 && urlType?.whatsapp_template_button_sub_type_field_option_id === 3 && (typeof websiteURL?.whatsapp_template_button_sub_type_field_value === "string") && websiteURL?.whatsapp_template_button_sub_type_field_value?.trim()?.length > 0) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    const getWebsiteUrlValue = (whatsapp_template_button_sub_type_field_data: any) => {
        const websiteUrl = whatsapp_template_button_sub_type_field_data?.find((subType: any) => {
            return subType?.field_name?.toLowerCase() === "website url";
        });
        if (websiteUrl) {
            return websiteUrl?.whatsapp_template_button_sub_type_field_value ?? "";
        }
        return "";
    }

    const handleChangeCallField = (event: any, field: any, id: any) => {
        const temp = actions?.map((ele: any) => {
            if (id === ele.id) {
                return {
                    ...ele,
                    [field]: event
                }
            }
            return ele
        })
        setActions(temp);
    }

    const handleChangeFieldVal = (value: number, ind: number, fieldIndex: number, btnTypeId: number, flagCarousel?: boolean) => {
        if (flagCarousel) {
            let val = [...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[0]?.whatsapp_template_button[ind].whatsapp_template_button_data]
            
            let selectedSubButton = whatsappTemplateButtonGenericData?.find((btn) => btn.whatsapp_template_button_type_id === btnTypeId)?.whatsapp_template_button_sub_type_data?.find((subType: any) => subType.whatsapp_template_button_sub_type_id === value);
            let isVisitWebsiteButton = selectedSubButton?.whatsapp_template_button_sub_type_name?.toLowerCase() === "visit website";
            
            const visitWebsiteSubTypeFields = whatsappTemplateButtonGenericData?.find((btn) => btn?.whatsapp_template_button_type_id === btnTypeId)?.whatsapp_template_button_sub_type_data?.find((subType: any) => subType?.whatsapp_template_button_sub_type_id === value)?.whatsapp_template_button_sub_type_field_data;

            let updatedVal = val.map((btn: any, i: number) => (i === fieldIndex) ? {
                ...btn,
                whatsapp_template_button_sub_type_id: value,
                whatsapp_template_button_sub_type_name: selectedSubButton?.whatsapp_template_button_sub_type_name,
                whatsapp_template_button_sub_type_field_data: visitWebsiteSubTypeFields?.map((field: any) => ({
                    ...field,
                    "whatsapp_template_button_sub_type_field_value": isVisitWebsiteButton && field?.field_name?.toLowerCase() === "website url" ? "https://" : "",
                    "whatsapp_template_button_sub_type_field_option_id": null,
                    ...(isVisitWebsiteButton && field?.field_name?.toLowerCase() === "url type" ? {
                        "whatsapp_template_button_sub_type_field_value": "Static",
                        "whatsapp_template_button_sub_type_field_option_id": field?.field_option_data?.find(
                            (option: any) => option?.field_option_value?.toLowerCase() === "static"
                        )?.field_option_id ?? 2,
                    } : {})
                }))
            } : btn);

            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_carousel: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                        whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.map((cards: any) => {
                            return {
                                ...cards,
                                whatsapp_template_button: cards?.whatsapp_template_button?.map((btn: any, index: number) => index === ind ? { ...btn, whatsapp_template_button_data: updatedVal } : btn)
                            }
                        })
                    }
                },
            });
        }
        else {
            let val = [...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button[ind]?.whatsapp_template_button_data];
            
            let selectedSubButton = whatsappTemplateButtonGenericData?.find((btn) => btn?.whatsapp_template_button_type_id === btnTypeId)?.whatsapp_template_button_sub_type_data?.find((subType: any) => subType?.whatsapp_template_button_sub_type_id === value);
            let isVisitWebsiteButton = selectedSubButton?.whatsapp_template_button_sub_type_name?.toLowerCase() === "visit website";

            let updatedVal = val.map((btn: any, i: number) => (i === fieldIndex) ? {
                ...btn,
                whatsapp_template_button_sub_type_id: value,
                whatsapp_template_button_sub_type_name: selectedSubButton?.whatsapp_template_button_sub_type_name,
                whatsapp_template_button_sub_type_field_data: whatsappTemplateButtonGenericData?.find((btn) => btn.whatsapp_template_button_type_id === btnTypeId)?.whatsapp_template_button_sub_type_data?.find((subType: any) => subType.whatsapp_template_button_sub_type_id === value)?.whatsapp_template_button_sub_type_field_data?.map((field: any) => ({
                    ...field,
                    "whatsapp_template_button_sub_type_field_value": isVisitWebsiteButton && field?.field_name.toLowerCase() === "website url" ? "https://" : "",
                    "whatsapp_template_button_sub_type_field_option_id": null,
                    ...(isVisitWebsiteButton && field?.field_name?.toLowerCase() === "url type" ? {
                        "whatsapp_template_button_sub_type_field_value": "Static",
                        "whatsapp_template_button_sub_type_field_option_id": 2,
                    } : {})
                }))
            } : btn);

            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_button: 
                        createTemplatePayload?.whatsapp_template_data?.whatsapp_template_button?.map((btn: any, index: number) => 
                            index === ind 
                            ? { 
                                ...btn, 
                                whatsapp_template_button_data: updatedVal 
                            } 
                            : btn
                        )
                },
            });
        }

        if (flagCarousel) {
            carouselInvalidId?.map((carousels: any) => {
                if (carousels?.inValidIds?.includes(String(ind + fieldIndex))) {
                    setCarouselInvalidId((prevCarousels: any) =>
                        prevCarousels?.map((carousel: any) => {
                            if (carousel?.index === carousels?.index) {
                                return {
                                    ...carousel,
                                    inValidIds: carousel?.inValidIds?.filter((ele: any) => ele !== String(ind + fieldIndex))
                                }
                            }
                            return carousel
                        })
                    )
                    inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                        if (refC?.index === carousels?.index) {
                            return {
                                ...refC,
                                inValids: refC?.inValids?.filter((ele: any) => ele !== String(ind + fieldIndex))
                            }
                        }
                        return refC
                    })
                }
                if (carousels?.inValidIds?.includes(fieldIndex)) {
                    setCarouselInvalidId((prevCarousels: any) =>
                        prevCarousels?.map((carousel: any) => {
                            if (carousel?.index === carousels?.index) {
                                return {
                                    ...carousel,
                                    inValidIds: carousel?.inValidIds?.filter((ele: any) => ele !== fieldIndex)
                                }
                            }
                            return carousel
                        })
                    )
                    inValidIdRefCarousel.current = inValidIdRefCarousel.current?.map((refC) => {
                        if (refC?.index === carousels?.index) {
                            return {
                                ...refC,
                                inValids: refC?.inValids?.filter((ele: any) => ele !== fieldIndex)
                            }
                        }
                        return refC
                    })
                }
            })
        }
        else {
            if (inValidId.includes(String(ind + fieldIndex))) {
                setInvalidId(inValidId?.filter((ele: any) => ele !== String(ind + fieldIndex)));
                inValidIdRef.current = inValidIdRef.current?.filter((ele: any) => ele !== String(ind + fieldIndex));
            }
            if (inValidId.includes(fieldIndex)) {
                setInvalidId(inValidId?.filter((ele: any) => ele !== fieldIndex));
                inValidIdRef.current = inValidIdRef.current.filter((ele: any) => ele !== fieldIndex);
            }
        }
    }

    const handleFormatting = (format: 'bold' | 'italic' | 'strikethrough' | 'monospace', type: string) => {
        // if (createTemplatePayload?.whatsapp_template_id) return;
        if(!canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draft_id)) return;
        if(isAuthenticationTemplate) return;
        if (
            !textareaRef.current &&
            type.toLowerCase() === "body") return;
        if (!headerTextareaRef.current && type.toLowerCase() === "header") return;


        const textarea = type.toLowerCase() === "body" ?
            textareaRef.current
            :
            headerTextareaRef.current;
        const value = type.toLowerCase() === "body" ? createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_value : createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_value;
        const start = (textarea as HTMLTextAreaElement).selectionStart;
        const end = (textarea as HTMLTextAreaElement).selectionEnd;

        let formattedText = '';
        let cursorAdjustment = 0;

        switch (format) {
            case 'bold':
                formattedText = `*${value.substring(start, end)}*`;
                cursorAdjustment = 1;
                break;
            case 'italic':
                formattedText = `_${value.substring(start, end)}_`;
                cursorAdjustment = 1;
                break;
            case 'strikethrough':
                formattedText = `~${value.substring(start, end)}~`;
                cursorAdjustment = 1;
                break;
            // case 'monospace':
            //     formattedText = `\`\`\`${value.substring(start, end)}\`\`\``;
            //     cursorAdjustment = 3;
            //     break;
            default:
                formattedText = value.substring(start, end);
        }

        const newValue = value?.substring(0, start) + formattedText + value?.substring(end);

        if (type.toLowerCase() === "body") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_body: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body,
                        whatsapp_template_body_value: newValue
                    }
                }
            });
        } else if (type.toLowerCase() === "header") {
            setCreateTemplatePayload({
                ...createTemplatePayload,
                whatsapp_template_data: {
                    ...createTemplatePayload?.whatsapp_template_data,
                    whatsapp_template_header: {
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header,
                        whatsapp_template_header_data: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.map((variable: any) => {
                            return {
                                ...variable,
                                whatsapp_template_header_data_value: newValue
                            }
                        })
                    }
                }
            });
        }

        // Reset cursor position
        setTimeout(() => {
            (textarea as HTMLTextAreaElement).setSelectionRange(start + cursorAdjustment, start + cursorAdjustment);
            (textarea as HTMLTextAreaElement).focus();
        }, 0);
    };

    const getHeaderFileFromStorage = async (storageId: number | string | null | undefined, filePath: string | null | undefined, headerType: string | null | undefined) => {
        if (!storageId || storageId === 0 || !headerType) return;

        try {
            const res = await axios.get(`${getVendorField(vendorData, "backend api", process.env.REACT_APP_BASE_URL)}/api/storage/file?file_id=${storageId}&flag_info=true`);

            if (res.data?.status) {
                const file = {
                    file_data: {
                        file_path: filePath ?? "",
                        flag_deleted: false,
                        flag_saved: false,
                        google_storage_file_name: res?.data?.data?.file_name,
                        google_storage_file_self_link: res?.data?.data?.self_link,
                        google_storage_flag_public: true,
                        google_storage_id: res?.data?.data?.google_storage_id,
                        google_storage_original_file_id: res?.data?.data?.storage_bucket_id,
                        google_storage_original_file_name: res?.data?.data?.original_file_name,
                        goolge_storage_file_size: res?.data?.data?.file_size,
                        goolge_storage_file_type: headerType?.toLowerCase() === "image" ? ".jpg" : ".mp4",
                        history_id: null,
                        timestamp: res?.data?.data?.uploaded_at,
                        user_assignment_id: res?.data?.data?.user_assignment_id,
                    },
                    google_storage_id: res?.data?.data?.google_storage_id,
                    isUploading: false,
                    previewImageSrc: getFileTypeImgIcon(headerType?.toLowerCase() === "image" ? "jpg" : "mp4"),
                    name: res?.data?.data?.original_file_name,
                    size: Number(res?.data?.data?.file_size),
                };

                setFiles([file]);
                setIsFileUploaded(true);
            }
        } catch {
            setFiles([]);
            setIsFileUploaded(false);
        }
    };


    // Authentication Template
    const handleAddExpiryTimeChange = (checked: boolean) => {
        
        setAddExpiryTimeForCode(checked);
    
        const existingExpirationMinutes = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_code_expiration_minutes;

        const expirationMinutes = typeof existingExpirationMinutes === "number" && existingExpirationMinutes >= 1 && existingExpirationMinutes <= 90 
            ? existingExpirationMinutes : DEFAULT_EXPIRATION_MINUTES;
        
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_code_expiration_minutes: checked ? expirationMinutes : null,
                whatsapp_template_footer: {  
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_footer,    
                    whatsapp_template_footer_value: checked ? renderAuthenticationTemplateFooterText(expirationMinutes) : "",
                },
            },
        });
    };

    const handleExpirationChange = (value: string) => {
        
        const expireMinutes = Number(value);
        const validExpirationMinutes = Number.isInteger(expireMinutes) && expireMinutes >= 1 && expireMinutes <= 90;
    
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_code_expiration_minutes: validExpirationMinutes ? expireMinutes : null,
                whatsapp_template_footer: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_footer,
                    whatsapp_template_footer_value: validExpirationMinutes ? renderAuthenticationTemplateFooterText(expireMinutes) : "",
                },
            },
        });
    };

    const handleSecurityRecommendationChange = (isChecked: boolean) => {
        const appendText = RENDER_SECURITY_RECOMMENDATION_APPEND_TEXT;
    
        const existingBody = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_value || "*{{1}}* is your verification code.";
    
        let updatedBody;
    
        if (isChecked) {
            updatedBody = existingBody.includes(appendText) ? existingBody : existingBody + " " + appendText;
        } else {
            updatedBody = existingBody.replace(appendText, "").trim();
        }
    
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_security_recommendation: isChecked,
                whatsapp_template_body: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body,
                    whatsapp_template_body_value: updatedBody,
                }
            }
        });
    };


    // Carousel
    const buildUploadedCarouselFileEntry = (sourceFile: any, responseData: any, fileTypeIconSrc: string) => ({
        ...sourceFile,
        name: sourceFile?.name,
        size: sourceFile?.size,
        isUploading: false,
        file_data: responseData,
        google_storage_id: responseData?.google_storage_id,
        previewImageSrc: fileTypeIconSrc,
    });

    const uploadFileHandlerCarousel = async (e: any, cardIndex: number) => {
        for (var i = 0; i < e.target.files.length; i++) {
            const file: any = e.target.files[i];

            file.isUploading = true;
            setCarouselFiles((prev: any[]) => {
                const exists = prev?.some((carouselFile) => carouselFile.index === cardIndex);
                const next = exists
                    ? prev?.map((carouselFile) => {
                        if (carouselFile.index === cardIndex) {
                            return { ...carouselFile, carousels: [file] };
                        }
                        return carouselFile;
                    })
                    : [...prev, { index: cardIndex, carousels: [file] }];
                return next;
            });

            let formData = new FormData()
            formData?.append(
                "file", file, file.name,
            )
            formData?.append("chatbot_flag", "true");

            let config = {
                onUploadProgress: (progressEvent: any) => {
                    const { loaded, total } = progressEvent;
                    var percent = Math.floor((loaded * 100) / total)
                    setProgress({ ...progress, percent: percent, filename: file.name })
                }
            }

            setFlagLoading({ ...flagLoading, flagRoundLoader: true })
            await agent.uploadFile.post(formData, config).then(async (res) => {
                if (res.status) {
                    file.isUploading = false;
                    file.file_data = res?.data?.data;

                    file.google_storage_id = res?.data?.data?.google_storage_id;
                    setCarouselFiles((prev: any[]) => {
                        const exists = prev?.some((carouselFile) => carouselFile.index === cardIndex);
                        const fileType = file.name.split('.').pop();
                        const fileTypeIconSrc = getFileTypeImgIcon(fileType) ?? "";

                        const next = exists
                            ? prev?.map((carouselFile) => {
                                if (carouselFile.index === cardIndex) {
                                    const updatedFiles = carouselFile?.carousels?.map((rr: any) => {
                                        if (rr.google_storage_id === file?.google_storage_id) {
                                            return buildUploadedCarouselFileEntry(rr, res?.data?.data, fileTypeIconSrc);
                                        }
                                        return rr;
                                    }) ?? [buildUploadedCarouselFileEntry(file, res?.data?.data, fileTypeIconSrc)];
                                    return { index: cardIndex, carousels: updatedFiles };
                                }
                                return carouselFile;
                            })
                            : [
                                ...prev,
                                {
                                    index: cardIndex,
                                    carousels: [buildUploadedCarouselFileEntry(file, res?.data?.data, fileTypeIconSrc)],
                                },
                            ];
                        return next;
                    });
                    setIsFileUploaded(false);
                    setCreateTemplatePayload({
                        ...createTemplatePayload,
                        whatsapp_template_data: {
                            ...createTemplatePayload?.whatsapp_template_data,
                            whatsapp_template_carousel: {
                                ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                                whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card.map((card: any, ind: number) => {
                                    if (ind === cardIndex) {
                                        const headerDataList = card?.whatsapp_template_header?.whatsapp_template_header_data?.length
                                            ? card.whatsapp_template_header.whatsapp_template_header_data
                                            : [{
                                                whatsapp_template_header_data_value: "",
                                                whatsapp_template_header_data_id: 0,
                                                whatsapp_template_header_data_google_storage_id: 0,
                                                whatsapp_template_header_variable_data: [],
                                            }];
                                        return {
                                            ...card,
                                            whatsapp_template_header: {
                                                ...card?.whatsapp_template_header,
                                                whatsapp_template_header_data: headerDataList.map((headerData: any) => ({
                                                    ...headerData,
                                                    whatsapp_template_header_data_value: res?.data?.data?.file_path,
                                                    whatsapp_template_header_data_google_storage_id: res?.data?.data?.google_storage_id,
                                                })),
                                            },
                                        };
                                    }
                                    return card;
                                })
                            }
                        }
                    });

                    let images = [];

                    for (let i = 0; i < e.target.files.length; i++) {
                        images.push(URL.createObjectURL(e.target.files[i]))
                    }
                }
            }).catch((err) => {
                showError("Something went wrong!")
                setFiles([])
                console.error(err)
            });

            setFlagLoading({ ...flagLoading, flagRoundLoader: false })
        }
    }

    const removeFileHandlerCarousel = async (storage_id: number, cardIndex: number) => {
        setFlagLoading({ ...flagLoading, flagRoundLoader: true })
        if (!(createTemplatePayload.whatsapp_template_id) && !searchParams.get('copy_template')) {
            removeUploadedFile(storage_id, true);
        }
        setCarouselFiles((prevFiles: any) =>
            prevFiles.map((file: any) => {
                if (file.index === cardIndex) {
                    return {
                        ...file,
                        carousels: file.carousels.filter((img: any) => img.google_storage_id !== storage_id)
                    };
                }
                return file;
            })
        );
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card.map((card: any, ind: number) => {
                        if (ind === cardIndex) {
                            const headerDataList = card?.whatsapp_template_header?.whatsapp_template_header_data?.length
                                ? card.whatsapp_template_header.whatsapp_template_header_data
                                : [{
                                    whatsapp_template_header_data_value: "",
                                    whatsapp_template_header_data_id: 0,
                                    whatsapp_template_header_data_google_storage_id: 0,
                                    whatsapp_template_header_variable_data: [],
                                }];
                            return {
                                ...card,
                                whatsapp_template_header: {
                                    ...card.whatsapp_template_header,
                                    whatsapp_template_header_data: headerDataList.map((headerData: any) => ({
                                        ...headerData,
                                        whatsapp_template_header_data_value: "",
                                        whatsapp_template_header_data_google_storage_id: 0,
                                    })),
                                },
                            };
                        }
                        return card;
                    }),
                },
            },
        });
        setFlagLoading({ ...flagLoading, flagRoundLoader: false })
    }

    const handleAddNewCard = () => {
        setCarouselFiles((prev: any[]) => {
            return [
                ...prev,
                {
                    index: prev.length,
                    carousels: []
                }
            ];
        })
        setCarouselInvalidId((prevCarousel: any) =>
            [...prevCarousel, {
                index: carouselInvalidId.length,
                inValidIds: []
            }]
        )

        inValidIdRefCarousel.current = [
            ...inValidIdRefCarousel.current,
            {
                index: carouselInvalidId.length,
                inValids: []
            }
        ]

        const firstCard = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card?.[0];
        const defaultHeader = {
            "whatsapp_template_header_type_id": firstCard?.whatsapp_template_header?.whatsapp_template_header_type_id ?? 0,
            "whatsapp_template_header_type_name": firstCard?.whatsapp_template_header?.whatsapp_template_header_type_name ?? null,
            "whatsapp_template_header_data": [
                {
                    "whatsapp_template_header_data_value": "",
                    "whatsapp_template_header_data_id": 0,
                    "whatsapp_template_header_data_google_storage_id": 0,
                    "whatsapp_template_header_variable_data": []
                }
            ]
        };

        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: [
                        ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card,
                        {
                            "whatsapp_template_header": defaultHeader,
                            "whatsapp_template_body": {
                                "whatsapp_template_body_value": "",
                                "whatsapp_template_body_description": "",
                                "whatsapp_template_body_variable_data": [],
                            },
                            "whatsapp_template_button": firstCard?.whatsapp_template_button?.length > 0 ? firstCard?.whatsapp_template_button?.map((fCardButton: any) => {
                                const isVisitWebsiteButton = fCardButton?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_name?.toLowerCase() === "visit website";

                                return {
                                    "whatsapp_template_button_id": getExistingActionButtonId(fCardButton?.whatsapp_template_button_type_id),
                                    "whatsapp_template_button_type_id": fCardButton?.whatsapp_template_button_type_id,
                                    "whatsapp_template_button_data": [
                                        {
                                            "whatsapp_template_button_data_id": getExistingActionButtonDataId(fCardButton?.whatsapp_template_button_type_id, fCardButton?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_id),
                                            "whatsapp_template_button_sub_type_id": fCardButton?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_id,
                                            "whatsapp_template_button_sub_type_name": fCardButton?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_name,
                                            "whatsapp_template_button_sub_type_field_data": whatsappTemplateButtonGenericData?.find((btn: any) => Number(btn.whatsapp_template_button_type_id) === Number(fCardButton?.whatsapp_template_button_type_id))
                                                ?.whatsapp_template_button_sub_type_data?.find((subType: any) => Number(subType.whatsapp_template_button_sub_type_id) === Number(fCardButton?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_id))
                                                ?.whatsapp_template_button_sub_type_field_data?.filter((field: any) => field?.field_name?.toLowerCase() !== "button description").map((field: any) => {
                                                    if (field?.field_name?.toLowerCase() === "payload") {
                                                        return {
                                                            ...field,
                                                            "whatsapp_template_button_sub_type_field_value": getExistingCardsPayload(fCardButton),
                                                            "whatsapp_template_button_sub_type_field_option_id": null,
                                                        };
                                                    }

                                                    if (isVisitWebsiteButton && field?.field_name?.toLowerCase() === "website url") {
                                                        return {
                                                            ...field,
                                                            "whatsapp_template_button_sub_type_field_value": "https://",
                                                            "whatsapp_template_button_sub_type_field_option_id": null,
                                                        };
                                                    }

                                                    if (isVisitWebsiteButton && field?.field_name?.toLowerCase() === "url type") {
                                                        return {
                                                            ...field,
                                                            "whatsapp_template_button_sub_type_field_value": "Static",
                                                            "whatsapp_template_button_sub_type_field_option_id": field?.field_option_data?.find(
                                                                (option: any) => option?.field_option_value?.toLowerCase() === "static"
                                                            )?.field_option_id ?? 2,
                                                        };
                                                    }

                                                    return {
                                                        ...field,
                                                        "whatsapp_template_button_sub_type_field_value": "",
                                                        "whatsapp_template_button_sub_type_field_option_id": null,
                                                    };
                                                })
                                        }
                                    ]
                                };
                            }) : [],
                            "whatsapp_template_footer": {
                                "whatsapp_template_footer_value": "",
                                "whatsapp_template_footer_id": null,
                            }
                        }
                    ] as whatsappCarouselCardData[]
                }
            }
        });
    };

    const handleDeleteCard = (cardIndex: number) => {
        if (createTemplatePayload?.whatsapp_template_id) {
            return;
        }

        const cards = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card ?? [];
        if (cards.length <= MIN_CAROUSEL_CARDS) {
            showError("Minimum 2 cards are required for carousel template")
            return;
        }

        setCarouselFiles((prev: any[]) => prev?.filter((file: any) => file.index !== cardIndex).map((file: any, idx: number) => ({ ...file, index: idx })));
        setCarouselInvalidId((prevCarousel: any) =>prevCarousel?.filter((_: any, idx: number) => idx !== cardIndex)?.map((item: any, idx: number) => ({ ...item, index: idx })));
        inValidIdRefCarousel.current = inValidIdRefCarousel.current?.filter((_: any, idx: number) => idx !== cardIndex)?.map((item: any, idx: number) => ({ ...item, index: idx }));

        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: cards?.filter((_: any, ind: number) => ind !== cardIndex),
                },
            },
        });
    };

    const handleChangeCardMediaHeader = (headers: any) => {
        if (createTemplatePayload?.whatsapp_template_id) return;
        
        const cards = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card ?? [];
        
        if (!copy_template) {
            cards?.forEach((card: any) => {
                const storageId = card?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_data_google_storage_id;
                if (storageId && storageId !== 0) {
                    removeUploadedFile(storageId, false);
                }
            });
        }

        setFiles([]);
        setCarouselFiles(cards?.map((_: any, cardIndex: number) => ({ index: cardIndex, carousels: [] })));
        setIsFileUploaded(false);
        
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: cards.map((card: any) => {
                        const headerDataList = card?.whatsapp_template_header?.whatsapp_template_header_data?.length
                            ? card?.whatsapp_template_header?.whatsapp_template_header_data
                            : [{
                                whatsapp_template_header_data_value: "",
                                whatsapp_template_header_data_id: 0,
                                whatsapp_template_header_data_google_storage_id: 0,
                                whatsapp_template_header_variable_data: [],
                            }];
                        return {
                            ...card,
                            whatsapp_template_header: {
                                ...card?.whatsapp_template_header,
                                whatsapp_template_header_type_id: headers?.whatsapp_template_header_type_id || 0,
                                whatsapp_template_header_type_name: headers?.whatsapp_template_header_type_name,
                                whatsapp_template_header_data: headerDataList?.map((headerData: any) => ({
                                    ...headerData,
                                    whatsapp_template_header_data_value: "",
                                    whatsapp_template_header_data_google_storage_id: 0,
                                })),
                            },
                        }
                    })
                }
            },
        });
    }

    const handleSampleTextChangeCarousel = (index: number, fieldIndex: number, value: string, isURL: boolean, cardIndex: number) => {
        setCreateTemplatePayload({
            ...createTemplatePayload,
            whatsapp_template_data: {
                ...createTemplatePayload?.whatsapp_template_data,
                whatsapp_template_carousel: {
                    ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel,
                    whatsapp_template_carousel_card: createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card.map((card: any, ind: number) => {
                        if (ind === cardIndex) {
                            return {
                                ...card,
                                whatsapp_template_button: card?.whatsapp_template_button?.map((btn: any, btnIndex: any) =>
                                    btnIndex === index
                                        ? {
                                            ...btn,
                                            whatsapp_template_button_data: btn?.whatsapp_template_button_data?.map(
                                                (field: any, fieldIdx: any) =>
                                                    fieldIdx === fieldIndex
                                                        ? {
                                                            ...field,
                                                            whatsapp_template_button_sub_type_field_data:
                                                                field?.whatsapp_template_button_sub_type_field_data?.map(
                                                                    (subType: any) =>
                                                                        subType.field_name.toLowerCase() === "website url"
                                                                            ? {
                                                                                ...subType,
                                                                                whatsapp_template_button_field_data_variable: [
                                                                                    {
                                                                                        whatsapp_template_dynamic_variable_key: "{{1}}",
                                                                                        whatsapp_template_dynamic_variable_sample_value: value,
                                                                                    },
                                                                                ],
                                                                            }
                                                                            : subType
                                                                ),
                                                        }
                                                        : field
                                            ),
                                        }
                                        : btn
                                ),
                            }
                        }
                        return card;
                    })
                }
            },
        });

        if (isURL) handleOnBlurOfDynamicVarOfBtn(value, "Website URL", fieldIndex, index, false, true, cardIndex);
    }

    // UseEffect
    const getCarouselHeaderFiles = async (id: string, file_path: string, headerType: string, cardIndex: number) => {
        if (id) {
            const res = await axios.get(`${getVendorField(vendorData, "backend api", process.env.REACT_APP_BASE_URL)}/api/storage/file?file_id=${id}&flag_info=true`);
            if (res.data.status) {
                const file = {
                    file_data: {
                        file_path: file_path,
                        flag_deleted: false,
                        flag_saved: false,
                        google_storage_file_name: res?.data?.data?.file_name,
                        google_storage_file_self_link: res?.data?.data?.self_link,
                        google_storage_flag_public: true,
                        google_storage_id: res.data.data?.google_storage_id,
                        google_storage_original_file_id: res?.data?.data?.storage_bucket_id,
                        google_storage_original_file_name: res?.data?.data?.original_file_name,
                        goolge_storage_file_size: res?.data?.data?.file_size,
                        goolge_storage_file_type: headerType.toLowerCase() === "image" ? ".jpg" : ".mp4",
                        history_id: null,
                        timestamp: res.data.data?.uploaded_at,
                        user_assignment_id: res.data.data?.user_assignment_id,
                    },
                    google_storage_id: res.data.data?.google_storage_id,
                    isUploading: false,
                    previewImageSrc: getFileTypeImgIcon(headerType.toLowerCase() === "image" ? "jpg" : "mp4"),
                    name: res.data.data?.original_file_name,
                    size: Number(res.data.data?.file_size),
                }
                let data = {
                    index: cardIndex,
                    carousels: [file]
                }
                setCarouselFiles((prev: any) => [
                    ...prev?.filter((item: any) => item.index !== cardIndex),
                    data,
                ]);
            }
            else {
                handleError(res?.data);
            }
        }
    }
    
    const initializeHeaderFilesFromPayload = async (payload: createWhatsappTemplatePayloadForTemplateLibrary | null | undefined) => {
        if (!payload) {
            setFiles([]);
            setCarouselFiles((prev: any[]) => {
                return [
                    ...prev,
                    {
                        index: prev.length,
                        carousels: []
                    }
                ];
            })
            setIsFileUploaded(false);
            return;
        }

        // Initialize header files for carousel template
        if (isCarouselTemplate(whatsappTemplateTypes, payload?.whatsapp_template_type_id)) {
            const cards = payload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card ?? [];
            const initialCarouselFiles = cards?.map((_: any, cardIndex: number) => ({ index: cardIndex, carousels: [] }));
            
            setCarouselFiles(initialCarouselFiles);
            setFiles([]);

            await Promise.all(
                cards.map((card: any, cardIndex: number) => {
                    const headerData = card?.whatsapp_template_header?.whatsapp_template_header_data?.[0];
                    const storageId = Number(headerData?.whatsapp_template_header_data_google_storage_id ?? 0);
                    const headerType = card?.whatsapp_template_header?.whatsapp_template_header_type_name;
                    
                    if (headerType && ALLOWED_MEDIA_HEADER_TYPES_CAROUSEL?.includes(headerType.toLowerCase()) && storageId > 0) {
                        return getCarouselHeaderFiles(
                            String(storageId),
                            headerData?.whatsapp_template_header_data_value ?? "",
                            headerType,
                            cardIndex,
                        );
                    }
                    return Promise.resolve();
                }),
            );
            return;
        }
        // Initialize header files for non-carousel template
        else {
            const header = payload?.whatsapp_template_data?.whatsapp_template_header;
            const headerType = header?.whatsapp_template_header_type_name ?? null;
            const headerData = header?.whatsapp_template_header_data?.[0] ?? null;
            const storageId = Number(headerData?.whatsapp_template_header_data_google_storage_id ?? 0);
            const headerValue = headerData?.whatsapp_template_header_data_value ?? null;

            if (headerType && ["image", "video", "document", "gif"].includes(headerType.toLowerCase()) && storageId > 0) {
                await getHeaderFileFromStorage(storageId, headerValue, headerType);
            } else {
                setFiles([]);
                setIsFileUploaded(false);
            }
        }
    };

    const payloadForCreateTemplate = (payload: createWhatsappTemplatePayloadForTemplateLibrary): createWhatsappTemplatePayloadForTemplateLibrary => {
        const { whatsapp_template_id, ...rest } = structuredClone(payload);
        return rest as createWhatsappTemplatePayloadForTemplateLibrary;
    }

    const ensureCustomBtnPayloadField = (fieldData: whatsappTemplateButtonSubTypeFieldData | whatsappTemplateButtonSubTypeFieldData[] | null | undefined, btnTypeName: string, btnSubTypeName: string, btnTypeId: number, btnSubTypeId: number) => {
        const fields = Array.isArray(fieldData) ? fieldData : fieldData ? [fieldData] : [];

        if (
            btnTypeName !== BTN_TYPE_NAMES['QUICK_REPLY'] ||
            btnSubTypeName !== BTN_SUB_TYPE_NAMES['CUSTOM'] ||
            fields.some((field) => field?.field_name === TEMPLATE_BUTTON_FIELDS['PAYLOAD'])
        ) {
            return fields;
        }

        const payloadField = whatsappTemplateButtonGenericData
            ?.find((btn: getWhatsappTemplateButtonTypeReponse) => btn.whatsapp_template_button_type_id === btnTypeId)
            ?.whatsapp_template_button_sub_type_data?.find(
                (subType: getWhatsappTemplateButtonSubTypeReponse) =>
                    subType.whatsapp_template_button_sub_type_id === btnSubTypeId,
            )
            ?.whatsapp_template_button_sub_type_field_data?.find(
                (field: getWhatsappTemplateButtonSubTypeFieldReponse) =>
                    field?.field_name === TEMPLATE_BUTTON_FIELDS['PAYLOAD'],
            );

        if (!payloadField) return fields;

        return [
            ...fields,
            {
                ...payloadField,
                whatsapp_template_button_sub_type_field_value: [],
            }
        ];
    };

    const mapTemplateDetailsToLibraryPayload = (updatedVal: any, flagImportTemplate: boolean = false): createWhatsappTemplatePayloadForTemplateLibrary => {

        const templateRows = Array.isArray(updatedVal?.whatsapp_template_data)
            ? updatedVal.whatsapp_template_data
            : updatedVal?.whatsapp_template_data
                ? [updatedVal.whatsapp_template_data]
                : [];
        const main = templateRows[0] ?? null;

        if (copy_template || flagImportTemplate) {
            const updated_value = {
                ...updatedVal,
                whatsapp_template_metadata: {
                    ...updatedVal.whatsapp_template_metadata,
                    meta_whatsapp_template_status_id: 0,
                    meta_whatsapp_template_status_name: "",
                }
            }
            setTemplateStatusDetails(updated_value);
        } else {
            setTemplateStatusDetails(updatedVal)
        }

        const mapButtonData = (buttonList: any) =>
            (Array.isArray(buttonList)
                ? buttonList
                : buttonList
                    ? [buttonList]
                    : []).map((btn: any) => ({
                whatsapp_template_button_id: btn?.whatsapp_template_button_id ?? null,
                whatsapp_template_button_type_id: btn?.whatsapp_template_button_type_id != null ? Number(btn?.whatsapp_template_button_type_id) : null,
                whatsapp_template_button_type_name: btn?.whatsapp_template_button_type_name ?? "",
                whatsapp_template_button_index: btn?.whatsapp_template_button_index ?? 0,
                flag_allow_tracking: btn?.flag_allow_tracking ?? false,
                whatsapp_template_button_data: (Array.isArray(btn?.whatsapp_template_button_data)
                    ? btn.whatsapp_template_button_data
                    : btn?.whatsapp_template_button_data
                        ? [btn.whatsapp_template_button_data]
                        : []).map(
                    (data: any) => ({
                        whatsapp_template_button_data_flag_deleted: data?.whatsapp_template_button_data_flag_deleted ?? "",
                        whatsapp_template_button_data_id: Number(data?.whatsapp_template_button_data_id ?? 0),
                        whatsapp_template_button_id: Number(data?.whatsapp_template_button_id ?? 0),
                        whatsapp_template_button_sub_type_id: Number(data?.whatsapp_template_button_sub_type_id ?? 0),
                        whatsapp_template_button_sub_type_name: data?.whatsapp_template_button_sub_type_name ?? "",
                        whatsapp_flow_data: data?.whatsapp_flow_data ? {
                            ...data.whatsapp_flow_data,
                            whatsapp_flow_id: data?.whatsapp_flow_data?.whatsapp_flow_id ?? null,
                            whatsapp_flow_name: data?.whatsapp_flow_data?.whatsapp_flow_name ?? "",
                            whatsapp_flow_description: data?.whatsapp_flow_data?.whatsapp_flow_description ?? "",
                            whatsapp_flow_meta_id: data?.whatsapp_flow_data?.whatsapp_flow_meta_id ?? null,
                            whatsapp_flow_preview: data?.whatsapp_flow_data?.whatsapp_flow_preview ? {
                                ...data?.whatsapp_flow_data?.whatsapp_flow_preview,
                                preview_url: data?.whatsapp_flow_data?.whatsapp_flow_preview?.preview_url ?? "",
                            } : data?.whatsapp_flow_data?.whatsapp_flow_preview
                        } : undefined,
                        whatsapp_template_button_sub_type_field_data: (ensureCustomBtnPayloadField(
                            data?.whatsapp_template_button_sub_type_field_data,
                            btn?.whatsapp_template_button_type_name ?? '',
                            data?.whatsapp_template_button_sub_type_name ?? '',
                            Number(btn?.whatsapp_template_button_type_id ?? 0),
                            Number(data?.whatsapp_template_button_sub_type_id ?? 0),
                        )).map(
                            (field: any) => {
                                const subFieldData = whatsappTemplateButtonGenericData
                                    ?.find(
                                        (genericBtn: any) => String(genericBtn?.whatsapp_template_button_type_id) === String(btn?.whatsapp_template_button_type_id)
                                    )
                                    ?.whatsapp_template_button_sub_type_data?.find(
                                        (subBtn: any) => String(subBtn?.whatsapp_template_button_sub_type_id) === String(data?.whatsapp_template_button_sub_type_id)
                                    )
                                    ?.whatsapp_template_button_sub_type_field_data?.find(
                                        (genericField: any) => String(genericField?.whatsapp_template_button_sub_type_field_id) === String(field?.whatsapp_template_button_sub_type_field_id)
                                    );

                                const flowFieldValue = (field?.field_name?.toLowerCase() === "flow" && data.whatsapp_flow_data?.whatsapp_flow_id != null)
                                    ? (data?.whatsapp_flow_data?.whatsapp_flow_id ?? field?.whatsapp_template_button_sub_type_field_value)
                                    : field?.whatsapp_template_button_sub_type_field_value;

                                return {
                                    ...(subFieldData ?? {}),
                                    whatsapp_template_button_field_data_id: Number(field?.whatsapp_template_button_field_data_id ?? 0),
                                    whatsapp_template_button_sub_type_field_id: Number(field?.whatsapp_template_button_sub_type_field_id ?? 0),
                                    whatsapp_template_button_sub_type_field_value: flowFieldValue,
                                    whatsapp_template_button_sub_type_field_option_id: Number(field?.whatsapp_template_button_sub_type_field_option_id ?? (subFieldData as any)?.whatsapp_template_button_sub_type_field_option_id ?? 0),
                                    whatsapp_template_button_field_data_flag_deleted: field?.whatsapp_template_button_field_data_flag_deleted ?? false,
                                    field_name: (subFieldData)?.field_name ?? field?.field_name ?? "",
                                    field_type: (subFieldData)?.field_type ?? field?.field_type ?? "",
                                    field_placeholder: (subFieldData)?.field_placeholder ?? field?.field_placeholder ?? null,
                                    field_static_value_flag: (subFieldData)?.field_static_value_flag ?? field?.field_static_value_flag ?? false,
                                    field_static_value: (subFieldData)?.field_static_value ?? field?.field_static_value ?? null,
                                    field_option_flag: (subFieldData)?.field_option_flag ?? field?.field_option_flag ?? false,
                                    field_meta_key_name: (subFieldData as any)?.field_meta_key_name ?? field?.field_meta_key_name ?? "",
                                    field_option_value: field?.field_option_value ?? (subFieldData as any)?.field_option_value ?? null,
                                    whatsapp_template_button_type_name: btn?.whatsapp_template_button_type_name ?? (subFieldData as any)?.whatsapp_template_button_type_name ?? "",
                                    whatsapp_template_button_sub_type_name: data?.whatsapp_template_button_sub_type_name ?? (subFieldData as any)?.whatsapp_template_button_sub_type_name ?? "",
                                    whatsapp_template_button_id: Number(btn.whatsapp_template_button_id ?? 0),
                                    whatsapp_template_button_index: Number(btn?.whatsapp_template_button_index ?? 0),
                                    whatsapp_template_button_field_data_variable: field?.whatsapp_template_button_field_data_variable ?? [],
                                    field_option_data: (subFieldData)?.field_option_data ?? field?.field_option_data ?? [],
                                };
                            }
                        ),
                    })
                ),
            }));

        const templateButtonData = mapButtonData(main?.whatsapp_template_button);

        const updatedHeader = main?.whatsapp_template_header
            ? { ...main?.whatsapp_template_header, whatsapp_template_header_type_name: main?.whatsapp_template_header?.whatsapp_template_header_type_name === null ? "None" : main?.whatsapp_template_header?.whatsapp_template_header_type_name }
            : undefined;

        const isCarouselByType =
            isCarouselTemplate(whatsappTemplateTypes, updatedVal?.whatsapp_template_type?.whatsapp_template_type_id)
            || isCarouselTemplate(whatsappTemplateTypes, updatedVal?.whatsapp_template_type_id);
        const isTemplateDataFromApi = Array.isArray(updatedVal?.whatsapp_template_data);
        const savedCarouselCards = isTemplateDataFromApi
            ? undefined
            : main?.whatsapp_template_carousel?.whatsapp_template_carousel_card;
        const hasSavedCarouselCards = Array.isArray(savedCarouselCards) && savedCarouselCards.length > 0;
        const isCarouselType = isCarouselByType;

        const mapCarouselCardRow = (cards: any) => ({
            whatsapp_template_header: cards?.whatsapp_template_header
                ? {
                    ...cards?.whatsapp_template_header,
                    whatsapp_template_header_type_id: Number(cards?.whatsapp_template_header?.whatsapp_template_header_type_id ?? 0),
                    whatsapp_template_header_type_name: cards?.whatsapp_template_header?.whatsapp_template_header_type_name ?? "",
                    whatsapp_template_header_data: (Array.isArray(cards?.whatsapp_template_header?.whatsapp_template_header_data)
                        ? cards?.whatsapp_template_header?.whatsapp_template_header_data
                        : cards?.whatsapp_template_header?.whatsapp_template_header_data
                            ? [cards?.whatsapp_template_header?.whatsapp_template_header_data]
                            : []
                    ).map((headerData: any) => ({
                        ...headerData,
                        whatsapp_template_header_data_google_storage_id: Number(headerData?.whatsapp_template_header_data_google_storage_id ?? 0),
                        whatsapp_template_header_variable_data: headerData?.whatsapp_template_header_variable_data ?? [],
                    })),
                }
                : cards?.whatsapp_template_header,
            whatsapp_template_body: cards?.whatsapp_template_body
                ? {
                    ...cards?.whatsapp_template_body,
                    whatsapp_template_body_id: String(cards?.whatsapp_template_body?.whatsapp_template_body_id ?? ""),
                    whatsapp_template_body_value: cards?.whatsapp_template_body?.whatsapp_template_body_value ?? "",
                    whatsapp_template_body_description: cards?.whatsapp_template_body?.whatsapp_template_body_description ?? "",
                    whatsapp_template_body_variable_data: cards?.whatsapp_template_body?.whatsapp_template_body_variable_data ?? [],
                }
                : cards?.whatsapp_template_body,
            whatsapp_template_button: mapButtonData(cards?.whatsapp_template_button),
            whatsapp_template_footer: cards?.whatsapp_template_footer
                ? {
                    ...cards?.whatsapp_template_footer,
                    whatsapp_template_footer_value: cards?.whatsapp_template_footer?.whatsapp_template_footer_value ?? "",
                }
                : cards?.whatsapp_template_footer,
        });

        const carouselCardsSource = isCarouselType
            ? (hasSavedCarouselCards ? savedCarouselCards : templateRows)
            : (hasSavedCarouselCards ? savedCarouselCards : []);

        const whatsappTemplateDisplayName = copy_template
            ? getCopyTemplateDisplayName(main?.whatsapp_template_display_name, main?.whatsapp_template_name)
            : (main?.whatsapp_template_display_name ?? main?.whatsapp_template_name ?? "");

        let payload: any = {
            whatsapp_template_id: copy_template ? null : updatedVal.whatsapp_template_id,
            meta_integration_whatsapp_id: Number(updatedVal.meta_integration_whatsapp_id),
            whatsapp_template_flag_interactive_message: updatedVal?.whatsapp_template_flag_interactive_message,
            whatsapp_template_flag_flow: updatedVal?.whatsapp_template_flag_flow,
            interactive_type_id: updatedVal?.interactive_type?.interactive_type_id ?? updatedVal?.interactive_type_id ?? null,
            interactive_list_button_text: (updatedVal as any)?.interactive_list_button_text ?? null,
            flag_named_parameter: (updatedVal as any)?.flag_named_parameter ?? false,
            whatsapp_template_type_id: updatedVal?.whatsapp_template_type?.whatsapp_template_type_id ?? updatedVal?.whatsapp_template_type_id,
            whatsapp_template_data: {
                whatsapp_template_data_id: main?.whatsapp_template_data_id ?? 0,
                whatsapp_template_category_id: main?.whatsapp_template_category_id ?? 0,
                whatsapp_language_id: main?.whatsapp_language_data?.whatsapp_language_id ?? main?.whatsapp_language_id ?? 0,
                whatsapp_template_name: copy_template ? "" : main?.whatsapp_template_name ?? "",
                whatsapp_template_description: main?.whatsapp_template_description ?? "",
                whatsapp_template_display_name: whatsappTemplateDisplayName,
                whatsapp_template_header: isCarouselType ? {} : (updatedHeader ?? main?.whatsapp_template_header),
                whatsapp_template_body: isCarouselType ? {} : (
                    main?.whatsapp_template_body
                    ? {
                        ...main?.whatsapp_template_body,
                        whatsapp_template_body_id: String(main?.whatsapp_template_body?.whatsapp_template_body_id ?? ""),
                        whatsapp_template_body_value: main?.whatsapp_template_body?.whatsapp_template_body_value ?? "",
                        whatsapp_template_body_description: main?.whatsapp_template_body?.whatsapp_template_body_description ?? "",
                        whatsapp_template_body_variable_data: main?.whatsapp_template_body?.whatsapp_template_body_variable_data ?? [],
                    }
                    : { whatsapp_template_body_id: "", whatsapp_template_body_value: "", whatsapp_template_body_description: "", whatsapp_template_body_variable_data: [] }
                ),
                whatsapp_template_button: isCarouselType ? [] : templateButtonData,
                whatsapp_template_footer: isCarouselType ? {} : (main?.whatsapp_template_footer),
                whatsapp_template_carousel: {
                    whatsapp_template_carousel_header:
                        main?.whatsapp_template_carousel?.whatsapp_template_carousel_header
                        ?? updatedVal?.whtatsapp_carousel_header
                        ?? "",
                    whatsapp_template_carousel_header_dynamic_variable:
                        main?.whatsapp_template_carousel?.whatsapp_template_carousel_header_dynamic_variable
                        ?? updatedVal?.whtatsapp_carousel_header_variable_data
                        ?? [],
                    whatsapp_template_carousel_card: isCarouselType
                        ? carouselCardsSource?.map(mapCarouselCardRow)
                        : carouselCardsSource,
                },
                whatsapp_template_security_recommendation: main?.whatsapp_template_security_recommendation ?? false,
                whatsapp_template_code_expiration_minutes: main?.whatsapp_template_code_expiration_minutes ?? null,
            },
        };

        return payload;
    };

    const updateTemplateStatus = (metaStatus?: string | null, isInteractive?: boolean | null, isDraft?: boolean | null) => {
        if (isDraft) {
            setTemplateStatus("Draft");
            return;
        }
        if (isInteractive) {
            setTemplateStatus("Approved");
            return;
        }
        setTemplateStatus(metaStatus ?? null);
    };

    const clearStaleTemplateState = () => {
        setIsPageReady(false);
        setCreateTemplatePayload(null as unknown as createWhatsappTemplatePayloadForTemplateLibrary);
        setExistingTemplateData(null);
        setExistingPayloadData(null);
        setExistingHeaderTemplateVariableData(null);
        setTemplateStatus(null);
        setTemplateStatusDetails(null);
        setFiles([]);
        setInvalidId([]);
        setInvalidPayloadObjectId(null);
        initialPayloadRef.current = null;
    };

    const markTemplatePageReady = () => {
        setIsPageReady(true);
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    };

    const loadExistingTemplateForEdit = async () => {
        if (!template_id && !copy_template) return;
        if (!id) return;
        if (initialPayloadRef.current) return;

        initialPayloadRef.current = {} as createWhatsappTemplatePayloadForTemplateLibrary;

        const response = await agent.getWhatsappTemplateDetails.get(Number(id), Number(template_id || copy_template));
        const data = response.data?.[0] ?? null;

        if (response.status && data) {

            const templateStatus = data?.whatsapp_template_metadata?.meta_whatsapp_template_status_name;
            const isInteractiveTemplate = data?.whatsapp_template_flag_interactive_message;
            updateTemplateStatus(templateStatus, isInteractiveTemplate, !!draft_id);
            
            let mapped = mapTemplateDetailsToLibraryPayload(data, false);
            
            if (copy_template && isCarouselTemplate(whatsappTemplateTypes, mapped?.whatsapp_template_type_id)) {
                mapped = normalizeTemplatePayloadForDraft(mapped, whatsappTemplateTypes ?? []);
            }

            if (mapped.whatsapp_template_flag_flow && !publishedFlowList) {
                await getFlowListApi();
            }
            if (mapped.whatsapp_template_flag_interactive_message && !interactiveTypeList) {
                await getInteractiveTypeList();
            }

            setCreateTemplatePayload(mapped);
            setExistingPayloadData(mapped);
            setExistingTemplateData(mapped);

            setExistingHeaderTemplateVariableData(mapped?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data);
            initialPayloadRef.current = structuredClone(mapped);
            setHasUnsavedChanges(false);
            await initializeHeaderFilesFromPayload(mapped);
            markTemplatePageReady();
        } else {
            initialPayloadRef.current = null;
            handleError(response);
            markTemplatePageReady();
        }
    };

    const loadTemplateFromDraft = async () => {
        if (!draft_id) return;

        let draftList = templateLibraryDraftList;
        if (!draftList) {
            await getTemplateLibraryDraftListApiCall();
            draftList = useTemplateLibraryStore.getState().templateLibraryDraftList;
        }

        // Keep the in-progress form if draft list is still unavailable (e.g. first save just added draft_id).
        if (!draftList) {
            if (initialPayloadRef.current || useTemplateLibraryStore.getState().createTemplatePayload) {
                updateTemplateStatus(null, false, true);
            }
            markTemplatePageReady();
            return;
        }
        
        const draft = draftList?.find((draft) => draft?.whatsapp_template_draft_id === draft_id);
        
        if (draft?.json_data) {
            const payload = JSON.parse(draft?.json_data) as createWhatsappTemplatePayloadForTemplateLibrary;
            const payloadForCreate = isCarouselTemplate(whatsappTemplateTypes, payload?.whatsapp_template_type_id) ?
                normalizeTemplatePayloadForDraft(mapTemplateDetailsToLibraryPayload(payload, false), whatsappTemplateTypes ?? []) :
                mapTemplateDetailsToLibraryPayload(payload, false);

            const isInteractiveTemplate = payloadForCreate?.whatsapp_template_flag_interactive_message;
            updateTemplateStatus(null, isInteractiveTemplate, true);

            if (payloadForCreate?.whatsapp_template_flag_flow && !publishedFlowList) {
                await getFlowListApi();
            }

            if(payloadForCreate?.whatsapp_template_flag_interactive_message && !interactiveTypeList) {
                await getInteractiveTypeList();
            }

            setCreateTemplatePayload(payloadForCreate);
            initialPayloadRef.current = structuredClone(payloadForCreate);
            setHasUnsavedChanges(false);
            await initializeHeaderFilesFromPayload(payloadForCreate);
        }
        else if (initialPayloadRef.current || useTemplateLibraryStore.getState().createTemplatePayload) {
            // Draft not in list yet after first save — keep current payload instead of resetting to scratch.
            updateTemplateStatus(null, false, true);
        }
        else {
            updateTemplateStatus(null, false, true);
            const payloadForCreate = structuredClone(createBasicTemplateFromScratch(templateLibraryCategoryList ?? [], whatsappTemplateButtonGenericData ?? []) as createWhatsappTemplatePayloadForTemplateLibrary);
            setCreateTemplatePayload(payloadForCreate);
            initialPayloadRef.current = structuredClone(payloadForCreate);
            setHasUnsavedChanges(false);
        }
        markTemplatePageReady();
    }

    const resetImportTemplateState = () => {
        setImportTemplatePayload(null);
        setImportTemplateName(null);
        setImportTemplateJson(null);
        setImportTemplateFile(null);
        setImportTemplateFileName(null);
        setImportTemplateSelectedTab(0);
    }

    const applyImportedTemplate = async () => {
        if (!importTemplatePayload) {
            return;
        }

        if (isInvalidExportFile(importTemplatePayload)) {
            showError("Invalid file")
            resetImportTemplateState();
            markTemplatePageReady();
            return;
        }

        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));

        try {
            const payloadForCreate = payloadForCreateTemplate(importTemplatePayload);
            const mapped = mapTemplateDetailsToLibraryPayload(payloadForCreate, true);

            setCreateTemplatePayload(mapped);
            setSelectedTemplate(null);
            setExistingPayloadData(mapped);
            setExistingTemplateData(mapped);

            setExistingHeaderTemplateVariableData(mapped?.whatsapp_template_data?.whatsapp_template_header?.whatsapp_template_header_data?.[0]?.whatsapp_template_header_variable_data);
            initialPayloadRef.current = structuredClone(mapped);
            setHasUnsavedChanges(false);
            await initializeHeaderFilesFromPayload(mapped);
            
            if (mapped?.whatsapp_template_flag_flow && !publishedFlowList) {
                await getFlowListApi();
            }
            markTemplatePageReady();
        } 
        catch (error) {
            showError("Error applying imported template.")
            markTemplatePageReady();
        } 
        finally {
            resetImportTemplateState();
            setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
        }
    };


    useLayoutEffect(() => {
        const previousDraftId = previousDraftIdRef.current;
        previousDraftIdRef.current = draft_id;
        
        const previousTemplateId = previousTemplateIdRef.current;
        previousTemplateIdRef.current = template_id;

        // First-time Save Draft only adds draft_id to the URL — keep the current form, don't remount into a loader.
        if (!previousDraftId && draft_id) {
            const currentPayload = useTemplateLibraryStore.getState().createTemplatePayload;
            if (currentPayload) {
                if (!initialPayloadRef.current) {
                    initialPayloadRef.current = structuredClone(currentPayload);
                }
                updateTemplateStatus(null, !!currentPayload?.whatsapp_template_flag_interactive_message, true);
                markTemplatePageReady();
                return;
            }
        }

        // Keep form when draft/template id is added or removed after submit / from-scratch (don't remount).
        if ((previousDraftId && !draft_id) || (!previousTemplateId && template_id) || (previousTemplateId && !template_id)) {
            const currentPayload = useTemplateLibraryStore.getState().createTemplatePayload;
            if (currentPayload) {
                initialPayloadRef.current = structuredClone(currentPayload);
                if (!template_id && !draft_id && !copy_template) {
                    setExistingHeaderTemplateVariableData(null);
                    setTemplateStatusDetails(null);
                    setFiles([]);
                    setInvalidId([]);
                    setInvalidPayloadObjectId(null);
                }
                markTemplatePageReady();
                return;
            }
        }

        clearStaleTemplateState();
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
    }, [template_id, draft_id, copy_template, id]);

    useLayoutEffect(() => {

        const hasTemplateMetadata = whatsappTemplateButtonGenericData?.length && whatsappTemplateTypes?.length && templateLibraryCategoryList?.length;

        if (template_id) {
            if (initialPayloadRef.current || !hasTemplateMetadata) {
                return;
            }
            loadExistingTemplateForEdit();
            return;
        }

        if (importTemplatePayload) {
            applyImportedTemplate();
            return;
        }

        if (draft_id) {
            if (initialPayloadRef.current || !hasTemplateMetadata) {
                return;
            }
            loadTemplateFromDraft();
            return;
        }

        if (copy_template) {
            if (initialPayloadRef.current || !hasTemplateMetadata) {
                return;
            }
            loadExistingTemplateForEdit();
            return;
        }

        if (selectedTemplate) {
            const payloadForCreate = payloadForCreateTemplate(selectedTemplate as createWhatsappTemplatePayloadForTemplateLibrary);
            setCreateTemplatePayload(payloadForCreate);
            initialPayloadRef.current = structuredClone(payloadForCreate);
            setHasUnsavedChanges(false);
            initializeHeaderFilesFromPayload(payloadForCreate);
            markTemplatePageReady();
            return;
        }

        if (!whatsappTemplateButtonGenericData || whatsappTemplateButtonGenericData.length === 0) {
            return;
        }

        if(initialPayloadRef.current) {
            return;
        }

        const payloadForCreate = structuredClone(createBasicTemplateFromScratch(templateLibraryCategoryList ?? [], whatsappTemplateButtonGenericData ?? []) as createWhatsappTemplatePayloadForTemplateLibrary);
        setCreateTemplatePayload(payloadForCreate);
        initialPayloadRef.current = structuredClone(payloadForCreate);
        setHasUnsavedChanges(false);
        markTemplatePageReady();

    }, [draft_id, templateLibraryDraftList, template_id, selectedTemplate, copy_template, whatsappTemplateButtonGenericData, whatsappTemplateTypes, templateLibraryCategoryList, importTemplatePayload]);
    
    useLayoutEffect(() => {
        (async () => {
            if (!useTemplateLibraryStore.getState().createTemplatePayload) {
                setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }))
            }

            if(!searchParams.has('template_id') && !searchParams.has('draft_id') && searchParams.has('id') && !hasTemplateManageAccess(apiRoutesPermissionAssignment, userDetail)){
                // Restrict user to create template if user don't have access.
                navigate(`/channels/viewintegration/whatsapp/templatemessages?id=${id}`);
            }

            if (searchParams.has('draft_id')) {
                await getTemplateLibraryDraftListApiCall();
            }

            const ensureData = async (data: unknown, fetcher: () => Promise<any>) => {
                if (!data) {
                    await fetcher();
                }
            };

            await ensureData(interactiveTypeList, getInteractiveTypeListApiCall);
            await ensureData(templateLibraryCategoryList, getTemplateLibraryCategoryListApiCall);
            await ensureData(whatsappTemplateButtonGenericData, getWhatsappTemplateButtonType);
            await ensureData(whatsappTemplateHeaderTypes, getWhatsappTemplateHeaderTypes);
            await ensureData(whatsappTemplateDynamicVarTypes, getWhatsappTemplateDynamicVarTypes);
            await ensureData(whatsappTemplateTypes, getWhatsappTemplateTypes);
            await ensureData(integrationListNewStructure, getMetaIntegrationList);

            const isEditingExisting = draft_id || template_id || copy_template;

            if (!isEditingExisting) {
                setCarouselFiles([]);
                setCarouselInvalidId([]);
                setFiles([]);
                setInvalidId([]);
                setIsFileUploaded(false);
            }

            // setFormOpen(true);

            if (isScreenBelow1250) {
                setFormOpen(false);
            } else {
                setFormOpen(true);
            }

            // Loader stays on until markTemplatePageReady() after the current template payload is set.

        })()

        return () => {
            clearStaleTemplateState();
        };
    }, []);


    // Render form and preview based on screen size
    const isFormInDrawer = formOpen && isScreenBelow1250;

    const renderForm = (
        isCarouselTemplate(whatsappTemplateTypes, createTemplatePayload?.whatsapp_template_type_id) ? 
        (
            <CarouselTemplateForm
                inDrawer={isFormInDrawer}
                carouselFiles={carouselFiles}
                handleChangeText={handleChangeText}
                handleClick={handleClick}
                handleClose={handleClose}
                menuPosition={menuPosition}
                anchorEl={anchorEl}
                getMenuDisableFlag={getMenuDisableFlag}
                getInput={getInput}
                inValidIdRefCarousel={inValidIdRefCarousel}
                handleChangeCallField={handleChangeCallField}
                handleChangeFieldVal={handleChangeFieldVal}
                isFileUploaded={isFileUploaded}
                uploadFileHandlerCarousel={uploadFileHandlerCarousel}
                removeFileHandlerCarousel={removeFileHandlerCarousel}
                handleAddNewCard={handleAddNewCard}
                handleDeleteCard={handleDeleteCard}
                flagShowVariableTooltipForVisitWebsite={flagShowVariableTooltipForVisitWebsite}
                checkForAddDynamicVar={checkForAddDynamicVar}
                carouselInvalidId={carouselInvalidId}
                setCarouselInvalidId={setCarouselInvalidId}
                handleSampleTextChangeCarousel={handleSampleTextChangeCarousel}
                getWebsiteUrlValue={getWebsiteUrlValue}
                handleChangeCardMediaHeader={handleChangeCardMediaHeader}
            />
        ) : (
            <WhatsAppTemplateForm
                inDrawer={isFormInDrawer}
                handleChangeText={handleChangeText}
                handleAddBodyVariableManually={handleAddBodyVariableManually}
                removeVariableData={removeVariableData}
                handleAddField={handleAddField}
                handleChange={handleChange}
                handleRemoveField={handleRemoveField}
                headerTextareaRef={headerTextareaRef}
                uploadFileHandler={uploadFileHandler}
                removeFileHandler={removeFileHandler}
                textareaRef={textareaRef}
                onChangeOfHeader={onChangeOfHeader}
                anchorEl={anchorEl}
                menuPosition={menuPosition}
                handleClick={handleClick}
                checkDisableAddButton={checkDisableAddButton}
                calculateMenuPosition={calculateMenuPosition}
                getMenuDisableFlag={getMenuDisableFlag}
                handleClose={handleClose}
                handleAddActionsButton={handleAddActionsButton}
                renderCustomButtonField={renderCustomButtonField}
                getInput={getInput}
                flagShowVariableTooltipForVisitWebsite={flagShowVariableTooltipForVisitWebsite}
                handleDeleteActionBtn={handleDeleteActionBtn}
                setOnChangeOfPayloadObject={setOnChangeOfPayloadObject}
                checkForAddDynamicVar={checkForAddDynamicVar}
                getWebsiteUrlValue={getWebsiteUrlValue}
                handleChangeCallField={handleChangeCallField}
                handleChangeFieldVal={handleChangeFieldVal}
                isFlowButton={isFlowButton}
                setPayloadOnChangeOfGetInput={setPayloadOnChangeOfGetInput}
                handleSampleTextChange={handleSampleTextChange}
                isFileUploaded={isFileUploaded}
                inValidId={inValidId}
                checkTypeOfVariableValidation={checkTypeOfVariableValidation}
                handleFormatting={handleFormatting}
                handleExpirationChange={handleExpirationChange}
                handleAddExpiryTimeChange={handleAddExpiryTimeChange}
                handleSecurityRecommendationChange={handleSecurityRecommendationChange}
                registerTextarea={registerTextarea}
            />
        )
    );

    const renderPreview = (
        isCarouselTemplate(whatsappTemplateTypes, createTemplatePayload?.whatsapp_template_type_id) ? 
        (
            <CarouselTemplatePreview
                // editable={totalButtons > 3 ? isEditMode : hover}
                mobile={isScreenBelow1250}
                editable={isEditMode}
                carouselFiles={carouselFiles}
                selectedTemplate={createTemplatePayload}
                handleChangeText={handleChangeText}
                handleClick={handleClick}
                handleClose={handleClose}
                menuPosition={menuPosition}
                anchorEl={anchorEl}
                getMenuDisableFlag={getMenuDisableFlag}
                getInput={getInput}
                inValidIdRefCarousel={inValidIdRefCarousel}
                handleChangeCallField={handleChangeCallField}
                handleChangeFieldVal={handleChangeFieldVal}
                isFileUploaded={isFileUploaded}
                uploadFileHandlerCarousel={uploadFileHandlerCarousel}
                removeFileHandlerCarousel={removeFileHandlerCarousel}
                handleAddNewCard={handleAddNewCard}
                handleDeleteCard={handleDeleteCard}
                flagShowVariableTooltipForVisitWebsite={flagShowVariableTooltipForVisitWebsite}
                checkForAddDynamicVar={checkForAddDynamicVar}
                setOnChangeOfPayloadObject={setOnChangeOfPayloadObject}
                carouselInvalidId={carouselInvalidId}
                setCarouselInvalidId={setCarouselInvalidId}
                handleSampleTextChangeCarousel={handleSampleTextChangeCarousel}
                getWebsiteUrlValue={getWebsiteUrlValue}
                handleChangeCardMediaHeader={handleChangeCardMediaHeader}
                calculateMenuPosition={calculateMenuPosition}
            />
        ) : (
            <WhatsAppTemplatePreview
                // editable={totalButtons > 3 ? isEditMode : hover}
                mobile={isScreenBelow1250}
                editable={isEditMode}
                selectedTemplate={createTemplatePayload}
                handleChangeText={handleChangeText}
                handleAddBodyVariableManually={handleAddBodyVariableManually}
                removeVariableData={removeVariableData}
                handleChange={handleChange}
                handleRemoveField={handleRemoveField}
                headerTextareaRef={headerTextareaRef}
                uploadFileHandler={uploadFileHandler}
                removeFileHandler={removeFileHandler}
                textareaRef={textareaRef}
                onChangeOfHeader={onChangeOfHeader}
                anchorEl={anchorEl}
                menuPosition={menuPosition}
                handleClick={handleClick}
                checkDisableAddButton={checkDisableAddButton}
                calculateMenuPosition={calculateMenuPosition}
                getMenuDisableFlag={getMenuDisableFlag}
                handleClose={handleClose}
                handleAddActionsButton={handleAddActionsButton}
                renderCustomButtonField={renderCustomButtonField}
                getInput={getInput}
                flagShowVariableTooltipForVisitWebsite={flagShowVariableTooltipForVisitWebsite}
                handleDeleteActionBtn={handleDeleteActionBtn}
                setOnChangeOfPayloadObject={setOnChangeOfPayloadObject}
                checkForAddDynamicVar={checkForAddDynamicVar}
                getWebsiteUrlValue={getWebsiteUrlValue}
                handleChangeCallField={handleChangeCallField}
                handleChangeFieldVal={handleChangeFieldVal}
                isFlowButton={isFlowButton}
                setPayloadOnChangeOfGetInput={setPayloadOnChangeOfGetInput}
                handleSampleTextChange={handleSampleTextChange}
                isFileUploaded={isFileUploaded}
                inValidId={inValidId}
                carouselInvalidId={carouselInvalidId}
                handleFormatting={handleFormatting}
                handleExpirationChange={handleExpirationChange}
                handleAddExpiryTimeChange={handleAddExpiryTimeChange}
                handleSecurityRecommendationChange={handleSecurityRecommendationChange}
            />
        )
    );

    const drawerSlideProps = {
        timeout: { enter: 320, exit: 260 },
        easing: { enter: "cubic-bezier(0.32, 0.72, 0, 1)", exit: "cubic-bezier(0.32, 0.72, 0, 1)" },
    };

    const drawerModalProps = {
        keepMounted: true,
        disableScrollLock: true,
    };

    const renderFormDrawer = isScreenBelow1250 ? (
        isMobileScreen ? (
            <SwipeableDrawer
                anchor="bottom"
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onOpen={() => setFormOpen(true)}
                disableSwipeToOpen
                disableDiscovery
                ModalProps={drawerModalProps}
                SlideProps={drawerSlideProps}
                transitionDuration={drawerSlideProps.timeout}
                PaperProps={{
                    className: `${styles.modifyTemplate__drawer} ${styles.modifyTemplate__drawerBottom}`,
                }}
            >
                <div className={styles.modifyTemplate__drawerHandle} />
                <div className={styles.modifyTemplate__drawerContent}>
                    {renderForm}
                </div>
            </SwipeableDrawer>
        ) : (
            <Drawer
                anchor="left"
                open={formOpen}
                onClose={() => setFormOpen(false)}
                ModalProps={drawerModalProps}
                SlideProps={drawerSlideProps}
                transitionDuration={drawerSlideProps.timeout}
                PaperProps={{
                    className: `${styles.modifyTemplate__drawer} ${styles.modifyTemplate__drawerLeft}`,
                }}
            >
                <div className={styles.modifyTemplate__drawerContent}>
                    {renderForm}
                </div>
            </Drawer>
        )
    ) : null;

    return (
        <>
            {isPageReady && createTemplatePayload ? (
            <>
            <Grid
                container
                xs={12}
                className={`${styles.modifyTemplate} ${globalStyles.fullwidth}`}
                sx={isScreenBelow1250 ? { height: `${innerHeight}px`, maxHeight: `${innerHeight}px`, overflow: "hidden", display: "flex", flexDirection: "column" } : undefined}
            >

                <Grid
                    item
                    xs={12}
                    sx={isScreenBelow1250 ? { flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" } : undefined}
                >
                
                    <ModifyWhatsAppTemplateHeader
                        checkTypeOfVariableValidation={checkTypeOfVariableValidation}
                        setHasUnsavedChanges={setHasUnsavedChanges}
                        initialPayloadRef={initialPayloadRef}
                        carouselFiles={carouselFiles}
                        carouselInvalidId={carouselInvalidId}
                        setCarouselFiles={setCarouselFiles}
                        setCarouselInvalidId={setCarouselInvalidId}
                        removeUploadedFile={removeUploadedFile}
                        getFlowListApi={getFlowListApi}
                        publishedFlowList={publishedFlowList}
                        getInteractiveTypeList={getInteractiveTypeList}
                    />

                    <Divider sx={{ width: "100%", opacity: "1", flexShrink: 0 }} />

                    <Grid
                        item
                        xs={12}
                        display="flex"
                        flexDirection={isScreenBelow1250 ? "column" : (formOpen ? "row" : "column")}
                        gap={isScreenBelow1250 ? 0 : (formOpen ? 1 : 0)}
                        justifyContent={isScreenBelow1250 ? "flex-start" : (formOpen ? "space-around" : "center")}
                        alignItems={isScreenBelow1250 ? "stretch" : (formOpen ? "stretch" : "center")}
                        flexWrap="nowrap"
                        className={`${globalStyles.fullwidth} ${isScreenBelow1250 ? styles.modifyTemplate__contentArea : ""}`}
                        sx={{
                            ...(isScreenBelow1250
                                ? { flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden" }
                                : {
                                    maxHeight: `${innerHeight}px`,
                                    minHeight: `${innerHeight}px`,
                                    height: `${innerHeight}px`,
                                    overflow: "hidden",
                                }),
                        }}
                    >
                        {/* LEFT - form (desktop) */}
                        {formOpen && !isScreenBelow1250 && (
                            <Grid
                                item
                                xs={isScreenBelow1600 ? 4.5 : 3.2}
                                display="flex"
                                flexDirection="column"
                                sx={{ maxHeight: "100%", height: "100%", overflowY: "auto" }}
                                className={styles.modifyTemplate__form}
                            >
                                {renderForm}
                            </Grid>
                        )}

                        {/* RIGHT - preview */}
                        <Grid
                            item
                            xs={isScreenBelow1250 ? 12 : (formOpen ? (isScreenBelow1600 ? 7.5 : 8.8) : 12)}
                            display="flex"
                            flexDirection="column"
                            justifyContent={isScreenBelow1250 ? "flex-start" : "center"}
                            alignItems="stretch"
                            gap={1}
                            position={isScreenBelow1250 ? "static" : (formOpen ? "sticky" : "static")}
                            sx={{
                                ...(isScreenBelow1250
                                    ? { width: "100%", minWidth: 0, flex: 1, minHeight: 0, maxHeight: "100%", height: "100%", overflow: "hidden" }
                                    : formOpen ? { minWidth: 400 } : { height: "100%" }),
                            }}
                            className={styles.modifyTemplate__preview}
                        >
                            
                            <Box 
                                className={styles.modifyTemplate__previewTitle} 
                                sx={{ width: "100%", textAlign: "center", flexShrink: 0, pt: isMobileScreen ? 0.5 : 1 }}
                            >
                                {!isMobileScreen && (
                                    <Typography
                                        variant="h6"
                                        className={`${globalStyles.primaryColor} ${styles.modifyTemplate__previewHeading}`}
                                        sx={{ mb: 0 }}
                                    >
                                        Build Your Message
                                    </Typography>
                                )}
                            </Box>

                            {/* {totalButtons > 3 && ( */}
                            {shouldShowPreviewEditModeButtons && (
                                <Box
                                    className={styles.modifyTemplate__previewModeToggle}
                                    sx={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Button
                                        variant="text"
                                        onClick={() => setIsEditMode(false)}
                                        className={`${styles.modifyTemplate__modeToggleButton} ${!isEditMode ? styles.modifyTemplate__modeToggleButtonActive : ""}`}
                                    >
                                        Preview Mode
                                    </Button>

                                    <Typography sx={{ px: 1, color: "text.secondary" }}>|</Typography>

                                    <Button
                                        variant="text"
                                        onClick={() => setIsEditMode(true)}
                                        className={`${styles.modifyTemplate__modeToggleButton} ${isEditMode ? styles.modifyTemplate__modeToggleButtonActive : ""}`}
                                    >
                                        Edit Mode
                                    </Button>
                                </Box>
                            )}
                            {/* )} */}

                            <Box
                                className={styles.modifyTemplate__previewCanvas}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    maxHeight: "100%",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    width: "100%",
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    // ref={previewHoverWrapRef}
                                    // onMouseEnter={() => setHover(true)}
                                    // onMouseLeave={() => setHover(false)}
                                    className={styles.modifyTemplate__previewCanvasInner}
                                    sx={{
                                        width: "100%",
                                        maxWidth: "100%",
                                        height: "100%",
                                        maxHeight: "100%",
                                        minHeight: 0,
                                        display: "flex",
                                        justifyContent: "center",
                                        overflow: "hidden",
                                    }}
                                >
                                    {renderPreview}
                                </Box>
                            </Box>
                        </Grid>

                    </Grid>

                </Grid>
            </Grid>

            {renderFormDrawer}
            </>
            ) : null}
        </>
    )

}

export default ModifyWhatsAppTemplate;
